import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/List'
import DescriptionIcon from '@mui/icons-material/Description'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HeightIcon from '@mui/icons-material/Height'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonIcon from '@mui/icons-material/Person'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Popover,
  Slider,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Chip,
} from '@mui/material'
import { ArrowLeftIcon } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useState, useEffect, useRef, memo } from 'react'
import { toast } from 'react-toastify'
import { EDVCSessionsCompletedDetailsComponent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import NewBookingHeaderButton from 'src/clinic/components/ClinicManageBookingsPage/headerComponents/NewBookingHeaderButton'
import {
  EDVCDateContainer,
  FloatingHeaderContainerDayCal,
  DateNavButton,
  TodayButton,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { sendDaySummaryToDoctor } from 'src/consumer/services/featureServices'
import { useScreenSize } from 'src/customHooks/customHooks'
import { StyledEventsRefreshIcon } from 'src/mentor/components/MentorEventsPage/MentorEventsPageStyles'
import MomentDatePicker from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/components/MomentDatePicker'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getSlotDurationBasedOnCheckIn, getEventTypesFromCheckInOption } from '../ClinicManageBookingsHelper'
import { getAllClinicRelatedEventTypes } from '../newBookingDialog/utils'
import { allAvailableEventTypes } from 'src/consumer/constants'
import SimplePractitionerButton from '../components/SimplePractitionerButton'

dayjs.extend(customParseFormat)

// Practitioner Avatar component (same as in ClinicManageBookingsPage)
const PractitionerAvatar = memo(({ imageUrl, managerId }) => {
  return (
    <Avatar
      src={imageUrl || undefined}
      sx={{
        width: 32,
        height: 32,
        bgcolor: 'rgba(116, 80, 170, 0.1)',
        color: 'rgba(116, 80, 170, 0.9)',
        border: '2px solid rgba(116, 80, 170, 0.2)',
        flexShrink: 0,
      }}
      imgProps={{
        style: {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        },
        onError: (e) => {
          // Prevent further error events - MUI Avatar will automatically show fallback
          e.target.onerror = null
          // Remove the src to prevent repeated load attempts
          e.target.src = ''
        },
        loading: 'eager',
        decoding: 'sync',
      }}
    >
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.imageUrl === nextProps.imageUrl &&
    prevProps.managerId === nextProps.managerId
  )
})

PractitionerAvatar.displayName = 'PractitionerAvatar'

// Old PractitionerButton removed - now using SimplePractitionerButton from shared component

export default function DayViewCalendarFloatingHeader({
  LeftSideExtraComponent,
  RightSideExtraComponent,

  forMentor,
  // Opt-in: hide this header's date picker (the host page shows it in its own main header).
  hideDatePicker = false,
  // Opt-in: hide this header's "more actions" (3-dots) overflow menu.
  hideOverflowMenu = false,
  // Opt-in: hide this header's filter pills (lifted into the host page's main header).
  hideFilterPills = false,
  selectedDate,
  mentorProfileData,
  mentorsList = [],
  onChangeMentorProfileData = (profileData) => {},
  scrollToPresentLine,
  setSelectedDate,
  isViewingCalendarRelatedTab,
  getAndSetBookedSlotsOfSelectedDate,
  bookedSlotsInDay,
  bookedSlotsInDayLoading,
  selectedDateUtcStartTime,
  selectedDateUtcEndTime,
  compactUI = false,
  headerContainerStyles = {},
  hideRefreshButton = false,
  cancelledCount,
  showCancelledOnly,
  setShowCancelledOnly,
  showCancelledChip,
  hideMentorSelection = false,
  hideScrollToTopButton = false,
  includeReportRelatedData,
  setIncludeReportRelatedData,
  hideToggleSwitchForReportRelatedData,
  hideDropdownForMentorSelection = false,
  selectedFilterType = 'all',
  setSelectedFilterType,
  onOpenBookedSlotsDrawer,
  onOpenPatientSearch,
  onOpenNewBooking,
  showNewBookingButton = false,
  onOpenPractitionerDrawer,
  practitionerButtonProps,
  clinicSchedules,
  eachSlotDurationInMins,
  setEachSlotDurationInMins,
  initialSlotDuration,
  thisClinicData,
  roomId,
  chambersData,
  resetSlotDurationToSystem,
  isSlotDurationManuallySet = false,
  isFloatingBarVisible = false,
}) {
  // When the host page has lifted everything out of this header (date picker, pills, overflow menu),
  // there is nothing left to show — collapse to nothing rather than leave an empty strip.
  const collapsed = forMentor && hideDatePicker && hideFilterPills && hideOverflowMenu
  const [sendReportLoading, setSendReportLoading] = useState(false)
  const { width } = useScreenSize()
  const [scrollButtonType, setScrollButtonType] = useState('down')
  const [currentTime, setCurrentTime] = useState(dayjs().format('hh:mm A'))
  const isMobile = width < 768
  
  // Dispatch calendar-date-change event when selectedDate changes
  // This ensures DayViewCalendarComponent receives the date change
  useEffect(() => {
    if (selectedDate) {
      window.dispatchEvent(new CustomEvent('calendar-date-change', { detail: { date: selectedDate } }))
    }
  }, [selectedDate])
  const showPractitionerInfo = true // Always show practitioner card - it's critical for clinic assistant flow
  
  // Scroll behavior state
  const [isMenuVisible, setIsMenuVisible] = useState(true)
  
  // Mobile overflow state kept for secondary actions when needed
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null)
  
  // CA Desktop 3-dots menu state
  const [caActionsMenuAnchor, setCaActionsMenuAnchor] = useState(null)

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget)
  }
  
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null)
  }
  
  const handleCaActionsMenuOpen = (event) => {
    event.stopPropagation()
    setCaActionsMenuAnchor(event.currentTarget)
  }
  
  const handleCaActionsMenuClose = () => {
    setCaActionsMenuAnchor(null)
  }
  
  // Toggle for showing floating bar after scroll stops
  // Default to enabled (true) - user can disable if they want
  const [showCalendarHeaderAfterScroll, setShowCalendarHeaderAfterScroll] = useState(() => {
    // Load from localStorage if available, default to true if not set
    const saved = localStorage.getItem('calendar-header-sticky-enabled')
    return saved !== null ? saved === 'true' : true
  })
  
  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('calendar-header-sticky-enabled', showCalendarHeaderAfterScroll.toString())
  }, [showCalendarHeaderAfterScroll])
  
  // Track if drawer is open
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  
  // Listen for drawer open/close events
  useEffect(() => {
    const handleDrawerChange = (event) => {
      setIsDrawerOpen(event.detail?.open || false)
    }
    
    window.addEventListener('drawer-opened', handleDrawerChange)
    
    return () => {
      window.removeEventListener('drawer-opened', handleDrawerChange)
    }
  }, [])

  // Check if calendar header is out of viewport and dispatch events
  useEffect(() => {
    let scrollTimeout = null
    
    const checkCalendarHeaderVisibility = () => {
      // Don't check if drawer is open
      if (isDrawerOpen) {
        return
      }
      
      const calendarHeader = document.querySelector('[data-calendar-header="true"]')
      if (calendarHeader) {
        const rect = calendarHeader.getBoundingClientRect()
        const isOutOfView = rect.bottom < 0
        
        if (showCalendarHeaderAfterScroll && isOutOfView) {
          // Toggle is enabled and header is out of view - show floating bar after scroll stops
          if (scrollTimeout) {
            clearTimeout(scrollTimeout)
          }
          scrollTimeout = setTimeout(() => {
            window.dispatchEvent(new CustomEvent('show-calendar-header-after-scroll', {
              detail: { show: true }
            }))
          }, 300)
        } else {
          // Toggle is disabled or header is in view - hide floating bar
          window.dispatchEvent(new CustomEvent('show-calendar-header-after-scroll', {
            detail: { show: false }
          }))
        }
      }
    }
    
    const handleScroll = () => {
      // Ignore scroll events when drawer is open
      if (isDrawerOpen) {
        return
      }
      
      // During scroll, hide floating bar if toggle is enabled
      if (showCalendarHeaderAfterScroll) {
        window.dispatchEvent(new CustomEvent('show-calendar-header-after-scroll', {
          detail: { show: false }
        }))
      }
      
      // Clear existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
      
      // After scroll stops, check and show if needed
      scrollTimeout = setTimeout(() => {
        checkCalendarHeaderVisibility()
      }, 300)
    }
    
    // Initial check
    checkCalendarHeaderVisibility()
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', checkCalendarHeaderVisibility, { passive: true })
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkCalendarHeaderVisibility)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [showCalendarHeaderAfterScroll, isDrawerOpen])
  
  // Dispatch event immediately when toggle changes
  useEffect(() => {
    const calendarHeader = document.querySelector('[data-calendar-header="true"]')
    if (calendarHeader) {
      const rect = calendarHeader.getBoundingClientRect()
      const isOutOfView = rect.bottom < 0
      
      if (showCalendarHeaderAfterScroll && isOutOfView) {
        // Toggle is enabled and header is out of view - show it immediately
        window.dispatchEvent(new CustomEvent('show-calendar-header-after-scroll', {
          detail: { show: true }
        }))
      } else {
        // Toggle is disabled or header is in view - hide it
        window.dispatchEvent(new CustomEvent('show-calendar-header-after-scroll', {
          detail: { show: false }
        }))
      }
    }
  }, [showCalendarHeaderAfterScroll])
  
  // Portal header detection state (for support portal and other portals)
  const [portalHeaderVisible, setPortalHeaderVisible] = useState(false)
  const [portalHeaderHeight, setPortalHeaderHeight] = useState(0)
  const portalHeaderCheckIntervalRef = useRef(null)
  // Use refs to track latest values for use in timeouts/callbacks
  const portalHeaderVisibleRef = useRef(false)
  const portalHeaderHeightRef = useRef(0)
  // Track if we've ever detected a portal header (to know if one exists even when hidden)
  const portalHeaderExistsRef = useRef(false)
  // Ref to the calendar header element itself
  const calendarHeaderRef = useRef(null)
  // Track last visibility state change time to prevent rapid toggling
  const lastVisibilityChangeRef = useRef(0)
  // Minimum time between visibility changes (ms)
  const MIN_VISIBILITY_CHANGE_INTERVAL = 200
  // Debounce timeout ref for portal header visibility changes
  const portalHeaderDebounceTimeoutRef = useRef(null)
  // Refs for syncing visibility (moved outside useEffect to persist across renders)
  const lastSyncValueRef = useRef(null)
  const syncTimeoutRef = useRef(null)
  
  // High Contrast state (for border visibility)
  const [highContrast, setHighContrast] = useState(() => {
    try {
      return localStorage.getItem('calendarHeaderHighContrast') === 'true'
    } catch {
      return false
    }
  })
  
  // Slot duration slider state
  const [slotDurationAnchorEl, setSlotDurationAnchorEl] = useState(null)
  const slotDurationPopoverOpen = Boolean(slotDurationAnchorEl)
  const syntheticAnchorRef = useRef(null)
  // Local state for slider value (not applied until user clicks Apply)
  const [localSlotDuration, setLocalSlotDuration] = useState(eachSlotDurationInMins || 30)
  // Flag to track if we just applied a change (to prevent reset on close)
  const justAppliedRef = useRef(false)
  // Debug info state
  const [showDebugInfo, setShowDebugInfo] = useState(false)
  
  // Slider range: 5 minutes to 120 minutes (2 hours)
  const MIN_SLOT_DURATION = 5
  const MAX_SLOT_DURATION = 120
  
  // Initialize local state when popover opens (but not when eachSlotDurationInMins changes after applying)
  useEffect(() => {
    if (slotDurationPopoverOpen && eachSlotDurationInMins !== undefined) {
      // Only reset if we're opening the popover, not if the value changed externally
      if (!justAppliedRef.current) {
        setLocalSlotDuration(eachSlotDurationInMins)
      }
    }
  }, [slotDurationPopoverOpen, eachSlotDurationInMins])
  
  // Sync localSlotDuration when eachSlotDurationInMins changes (but not when popover is open and we just applied)
  useEffect(() => {
    if (!slotDurationPopoverOpen && !justAppliedRef.current && eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }, [eachSlotDurationInMins, slotDurationPopoverOpen])
  
  // Reset the flag when popover closes
  useEffect(() => {
    if (!slotDurationPopoverOpen) {
      justAppliedRef.current = false
    }
  }, [slotDurationPopoverOpen])
  
  const handleSlotDurationClick = (event) => {
    setSlotDurationAnchorEl(event.currentTarget)
    // Reset local state to current value when opening
    if (eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }
  
  // Expose function to open drawer programmatically
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openSlotDurationDrawer = (event) => {
        // Use the button that triggered it, or create a fallback anchor
        if (event && event.currentTarget) {
          setSlotDurationAnchorEl(event.currentTarget)
        } else {
          // Find the header button or use body as fallback
          const headerButton = document.querySelector('[data-slot-duration-trigger]')
          if (headerButton) {
            setSlotDurationAnchorEl(headerButton)
          } else {
            // Create a synthetic anchor element
            const syntheticAnchor = document.createElement('div')
            syntheticAnchor.style.position = 'fixed'
            syntheticAnchor.style.top = '50%'
            syntheticAnchor.style.right = '20px'
            syntheticAnchor.style.zIndex = '9999'
            document.body.appendChild(syntheticAnchor)
            setSlotDurationAnchorEl(syntheticAnchor)
            // Clean up after popover closes
            setTimeout(() => {
              if (syntheticAnchor.parentNode) {
                syntheticAnchor.parentNode.removeChild(syntheticAnchor)
              }
            }, 100)
          }
        }
      }
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.openSlotDurationDrawer
      }
    }
  }, [])
  
  const handleSlotDurationClose = () => {
    setSlotDurationAnchorEl(null)
    // Clean up synthetic anchor if it exists
    if (syntheticAnchorRef.current && syntheticAnchorRef.current.parentNode) {
      syntheticAnchorRef.current.parentNode.removeChild(syntheticAnchorRef.current)
      syntheticAnchorRef.current = null
    }
    // Reset local state to current value when closing without applying
    if (!justAppliedRef.current && eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }
  
  const handleSlotDurationChange = (event, newValue) => {
    // Only update local state, don't apply to background yet
    setLocalSlotDuration(newValue)
  }
  
  const handleApplySlotDuration = () => {
    // Apply the local value to the actual state
    if (setEachSlotDurationInMins && localSlotDuration !== undefined) {
      justAppliedRef.current = true
      setEachSlotDurationInMins(localSlotDuration)
      // Close the popover after a short delay to show the change
      setTimeout(() => {
        setSlotDurationAnchorEl(null)
      }, 300)
    }
  }
  
  const handleResetSlotDuration = () => {
    // Reset to system-determined value
    if (resetSlotDurationToSystem) {
      resetSlotDurationToSystem()
      // Update local slider value to match
      // The system will recalculate and update eachSlotDurationInMins
      // We'll sync localSlotDuration when the popover reopens or when eachSlotDurationInMins changes
      setTimeout(() => {
        if (eachSlotDurationInMins !== undefined) {
          setLocalSlotDuration(eachSlotDurationInMins)
        }
      }, 100)
    } else if (initialSlotDuration !== undefined) {
      // Fallback to initial duration if reset function not available
      setLocalSlotDuration(initialSlotDuration)
    }
  }

  // Get debug info about slot duration calculation
  const getSlotDurationDebugInfo = () => {
    try {
      // Get check-in event option from localStorage
      let checkInEventOptionId = null
      let checkInEventOptions = null
      
      try {
        const checkedInDetails = localStorage.getItem('checkedInDetails')
        if (checkedInDetails) {
          const parsed = JSON.parse(checkedInDetails)
          checkInEventOptionId = parsed?.selectedCheckInEventOption
        }
      } catch (error) {
        console.error('Error reading check-in details:', error)
      }

      // Try to get checkInEventOptions from chambersData if roomId is available
      if (roomId && chambersData && Array.isArray(chambersData)) {
        const currentRoom = chambersData.find((room) => room?.id === roomId)
        if (currentRoom?.checkInEventOptions) {
          checkInEventOptions = currentRoom.checkInEventOptions
        }
      }

      // Get roomDataEventTypes from localStorage
      let roomDataEventTypes = null
      try {
        const storedRoomDataEventTypes = localStorage.getItem('roomDataEventTypes')
        if (storedRoomDataEventTypes) {
          roomDataEventTypes = JSON.parse(storedRoomDataEventTypes)
        }
      } catch (error) {
        console.error('Error reading roomDataEventTypes:', error)
      }

      const clinicId = thisClinicData?.id
      const clinicEventTypes = getAllClinicRelatedEventTypes(mentorProfileData?.eventTypes, clinicId)
      
      // Find matching event types from check-in option
      let matchingEvents = []
      let selectedOption = null
      let detectedCheckInOptionType = null
      
      // Merge roomDataEventTypes with mentorProfileData eventTypes for matching
      let availableEventTypes = mentorProfileData?.eventTypes || []
      if (roomDataEventTypes && roomDataEventTypes.length > 0) {
        const roomDataEventTypeIds = new Set(roomDataEventTypes.map(et => et?.uid || et?.id))
        const mentorEventTypesNotInRoom = (mentorProfileData?.eventTypes || []).filter(
          et => !roomDataEventTypeIds.has(et?.uid || et?.id)
        )
        availableEventTypes = [...roomDataEventTypes, ...mentorEventTypesNotInRoom]
      }

      if (checkInEventOptionId && checkInEventOptions && Array.isArray(checkInEventOptions)) {
        selectedOption = checkInEventOptions.find((option) => option?.id === checkInEventOptionId)
        if (selectedOption) {
          matchingEvents = getEventTypesFromCheckInOption(
            selectedOption,
            availableEventTypes, // Use merged eventTypes
            clinicId
          )
          
          // Detect check-in option type from title
          const optionTitle = (selectedOption.title || selectedOption.name || '').toLowerCase()
          
          // Check for screening-and-therapy first (most specific)
          if (optionTitle.includes('screening') && optionTitle.includes('therapy')) {
            detectedCheckInOptionType = 'screening-and-therapy'
          } 
          // Check for therapy-only
          else if (optionTitle.includes('therapy') && optionTitle.includes('only')) {
            detectedCheckInOptionType = 'therapy-only'
          } 
          // Check for screening-only (check for "screening" and "only" separately, or "screening-only")
          else if (optionTitle.includes('screening')) {
            if (optionTitle.includes('only') || optionTitle.includes('screening-only')) {
              detectedCheckInOptionType = 'screening-only'
            } else if (!optionTitle.includes('therapy')) {
              // If it has "screening" but not "therapy", assume screening-only
              detectedCheckInOptionType = 'screening-only'
            }
          }
        }
      }
      
      // If selectedOption not found but checkInEventOptionId looks like a type identifier, try to detect from ID
      if (!selectedOption && checkInEventOptionId) {
        const checkInEventOptionIdLower = checkInEventOptionId.toLowerCase()
        if (checkInEventOptionIdLower.includes('screening') && checkInEventOptionIdLower.includes('therapy')) {
          detectedCheckInOptionType = 'screening-and-therapy'
        } else if (checkInEventOptionIdLower.includes('therapy') && checkInEventOptionIdLower.includes('only')) {
          detectedCheckInOptionType = 'therapy-only'
        } else if (checkInEventOptionIdLower.includes('screening')) {
          if (checkInEventOptionIdLower.includes('only') || checkInEventOptionIdLower === 'screening-only') {
            detectedCheckInOptionType = 'screening-only'
          } else if (!checkInEventOptionIdLower.includes('therapy')) {
            detectedCheckInOptionType = 'screening-only'
          }
        }
      }
      
      // If we have a detected type but no matching events yet, find them from availableEventTypes
      if (matchingEvents.length === 0 && detectedCheckInOptionType) {
        
        // If no matching events found but we detected a type, find events manually
        // Use availableEventTypes which includes roomDataEventTypes
        if (detectedCheckInOptionType === 'screening-only') {
          matchingEvents = availableEventTypes.filter(
            (et) => et?.enabled &&
                    et?.contextId === clinicId &&
                    et?.type === allAvailableEventTypes.screening
          )
        } else if (detectedCheckInOptionType === 'therapy-only') {
          matchingEvents = availableEventTypes.filter(
            (et) => et?.enabled &&
                    et?.contextId === clinicId &&
                    (et?.type === allAvailableEventTypes.therapySession || et?.type === allAvailableEventTypes.assessment)
          )
        } else if (detectedCheckInOptionType === 'screening-and-therapy') {
          matchingEvents = availableEventTypes.filter(
            (et) => et?.enabled &&
                    et?.contextId === clinicId &&
                    (et?.type === allAvailableEventTypes.screening ||
                     et?.type === allAvailableEventTypes.therapySession ||
                     et?.type === allAvailableEventTypes.assessment)
          )
        }
      }

      // If no matching events from check-in option, try direct match
      // Use availableEventTypes which includes roomDataEventTypes
      if (matchingEvents.length === 0 && checkInEventOptionId && availableEventTypes.length > 0) {
        const checkInEventType = availableEventTypes.find(
          (et) => (et?.uid === checkInEventOptionId || et?.id === checkInEventOptionId) &&
                  et?.enabled &&
                  et?.contextId === clinicId
        )
        if (checkInEventType) {
          matchingEvents = [checkInEventType]
        }
      }

      const eventTypeUsed = matchingEvents.length > 0 
        ? matchingEvents.reduce((min, event) => 
            (!min || (event?.durationInMins && event.durationInMins < min.durationInMins)) ? event : min
          )
        : null

      const DEFAULT_DURATIONS = {
        [allAvailableEventTypes.consultation]: 15,
        [allAvailableEventTypes.therapySession]: 30,
        [allAvailableEventTypes.screening]: 15,
        [allAvailableEventTypes.oneOnOne]: 20,
        [allAvailableEventTypes.assessment]: 30,
        [allAvailableEventTypes.interview]: 60,
      }

      // Get all event types grouped by type for display
      // Include roomDataEventTypes in the calculation
      let allClinicEventTypes = (mentorProfileData?.eventTypes || []).filter(
        (et) => et?.enabled && et?.contextId === clinicId
      )
      
      // Merge with roomDataEventTypes if available
      if (roomDataEventTypes && roomDataEventTypes.length > 0) {
        const roomDataEventTypeIds = new Set(roomDataEventTypes.map(et => et?.uid || et?.id))
        const mentorEventTypesNotInRoom = allClinicEventTypes.filter(
          et => !roomDataEventTypeIds.has(et?.uid || et?.id)
        )
        allClinicEventTypes = [...roomDataEventTypes, ...mentorEventTypesNotInRoom]
      }
      
      const eventTypesByType = {}
      allClinicEventTypes.forEach((et) => {
        const type = et?.type || 'unknown'
        if (!eventTypesByType[type]) {
          eventTypesByType[type] = []
        }
        eventTypesByType[type].push(et)
      })

      // Calculate slot sizes for each event type
      const slotSizesByType = {}
      Object.keys(eventTypesByType).forEach((type) => {
        const eventsOfType = eventTypesByType[type]
        const durations = eventsOfType
          .map((e) => e?.durationInMins)
          .filter((d) => d != null)
        
        if (durations.length > 0) {
          slotSizesByType[type] = {
            min: Math.min(...durations),
            max: Math.max(...durations),
            events: eventsOfType,
            default: DEFAULT_DURATIONS[type] || null,
          }
        } else {
          slotSizesByType[type] = {
            min: null,
            max: null,
            events: eventsOfType,
            default: DEFAULT_DURATIONS[type] || null,
          }
        }
      })

      // If we still don't have detectedCheckInOptionType but have matching events, infer from event types
      if (!detectedCheckInOptionType && matchingEvents.length > 0) {
        const eventTypes = [...new Set(matchingEvents.map(e => e.type))]
        const hasScreening = eventTypes.includes(allAvailableEventTypes.screening)
        const hasTherapy = eventTypes.includes(allAvailableEventTypes.therapySession)
        const hasAssessment = eventTypes.includes(allAvailableEventTypes.assessment)
        
        if (hasScreening && hasTherapy || hasScreening && hasAssessment || hasTherapy && hasAssessment) {
          detectedCheckInOptionType = 'screening-and-therapy'
        } else if (hasTherapy || hasAssessment) {
          detectedCheckInOptionType = 'therapy-only'
        } else if (hasScreening) {
          detectedCheckInOptionType = 'screening-only'
        }
      }

      return {
        checkInEventOptionId,
        selectedOption,
        detectedCheckInOptionType,
        eventTypeUsed,
        matchingEvents,
        clinicId,
        clinicEventTypes,
        calculatedDuration: getSlotDurationBasedOnCheckIn(
          mentorProfileData,
          clinicId,
          checkInEventOptionId,
          checkInEventOptions,
          roomDataEventTypes,
        ),
        roomDataEventTypes: roomDataEventTypes || null,
        slotSizesByType,
        allClinicEventTypes,
      }
    } catch (error) {
      console.error('Error getting debug info:', error)
      return null
    }
  }
  
  // Format minutes to readable string
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`
    } else {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      if (mins === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
      }
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${mins} min`
    }
  }
  function onPrevClick() {
    const newDate = dayjs(selectedDate, 'DD-MM-YYYY').subtract(1, 'day').format('DD-MM-YYYY')
    setSelectedDate(newDate)
  }

  function onNextClick() {
    const newDate = dayjs(selectedDate, 'DD-MM-YYYY').add(1, 'day').format('DD-MM-YYYY')
    setSelectedDate(newDate)
  }

  async function sendDayReportForDoctor() {
    setSendReportLoading(true)
    const response = await sendDaySummaryToDoctor(selectedDateUtcStartTime, selectedDateUtcEndTime)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      toast.success("Day's summary sent to your preferred communication mediums.")
    } else {
      toast.error('Error sending summary to your preferred communication mediums')
    }
    setSendReportLoading(false)
  }

  const isToday = dayjs().format('DD-MM-YYYY') === selectedDate
  const selectedDateDayjs = dayjs(selectedDate, 'DD-MM-YYYY')
  const mobileDateButtonLabel = isToday
    ? `Today · ${selectedDateDayjs.format('D MMM')}`
    : `${selectedDateDayjs.format('ddd')} · ${selectedDateDayjs.format('D MMM')}`
  
  const handleGoToToday = () => {
    setSelectedDate(dayjs().format('DD-MM-YYYY'))
  }

  // Update current time every minute
  useEffect(() => {
    // Set initial time
    setCurrentTime(dayjs().format('hh:mm A'))
    
    // Update every minute
    const interval = setInterval(() => {
      setCurrentTime(dayjs().format('hh:mm A'))
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  // Detect portal header (support portal or other portals) and its visibility
  useEffect(() => {
    // Track previous values to only log on changes
    let prevVisible = null
    let prevHeight = null
    
    // Check if we're in a portal context by looking for portal headers in the DOM
    const detectPortalHeader = () => {
      // Check for portal headers using data attribute (works for support portal and other portals)
      const portalHeader = document.querySelector('[data-portal-header="true"]')
      
      if (portalHeader) {
        const rect = portalHeader.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(portalHeader)
        const isVisible = computedStyle.opacity !== '0' && 
                         computedStyle.visibility !== 'hidden' && 
                         computedStyle.transform !== 'translateY(-100%)' &&
                         rect.height > 0
        
        // Always use the actual height of the portal header element for positioning
        // This ensures calendar header is positioned correctly even if portal header is temporarily hidden
        // Use offsetHeight as fallback if getBoundingClientRect height is 0 (e.g., when hidden)
        const actualHeight = rect.height > 0 ? rect.height : (portalHeader.offsetHeight || 0)
        const newHeight = actualHeight
        
        // Mark that portal header exists (even if currently hidden)
        portalHeaderExistsRef.current = true
        
        // Only update state if values actually changed
        // IMPORTANT: Portal header visibility should only affect positioning (top), not calendar header visibility
        // Add throttling to prevent rapid state changes, but only throttle height changes, not visibility
        if (prevVisible !== isVisible || prevHeight !== newHeight) {
          const now = Date.now()
          const timeSinceLastChange = now - lastVisibilityChangeRef.current
          
          // For height changes, throttle to prevent rapid updates
          // For visibility changes, update immediately but only if height also changed or it's the first time
          const shouldUpdate = timeSinceLastChange >= MIN_VISIBILITY_CHANGE_INTERVAL || 
                              prevVisible === null ||
                              (prevHeight !== newHeight) // Always update if height changed
          
          if (shouldUpdate) {
            lastVisibilityChangeRef.current = now
            
            // Update height immediately (affects positioning)
            setPortalHeaderHeight(newHeight)
            portalHeaderHeightRef.current = newHeight
            
            // Update visibility, but don't let it trigger calendar header visibility changes
            // Portal header visibility only affects positioning, not calendar header visibility
            setPortalHeaderVisible(isVisible)
            portalHeaderVisibleRef.current = isVisible
            
            prevVisible = isVisible
            prevHeight = newHeight
          }
        }
      } else {
        // Only update if we previously had a header (removed console.log to reduce noise)
        if (prevVisible !== false || prevHeight !== 0) {
          setPortalHeaderVisible(false)
          setPortalHeaderHeight(0)
          // Update refs for use in callbacks/timeouts
          portalHeaderVisibleRef.current = false
          portalHeaderHeightRef.current = 0
          prevVisible = false
          prevHeight = 0
        }
      }
    }
    
    // Initial detection
    detectPortalHeader()
    
    // Listen for portal header visibility changes via custom events (for height updates only)
    // NOTE: This is ONLY for updating height (positioning), NOT for visibility sync
    // Visibility sync is handled separately below
    const handlePortalHeaderChange = (event) => {
      // Only update height if provided in event detail
      if (event.detail?.height !== undefined) {
        const now = Date.now()
        const timeSinceLastChange = now - lastVisibilityChangeRef.current
        
        // Throttle height updates
        if (timeSinceLastChange >= MIN_VISIBILITY_CHANGE_INTERVAL) {
          lastVisibilityChangeRef.current = now
          setPortalHeaderHeight(event.detail.height)
          portalHeaderHeightRef.current = event.detail.height
        }
      } else {
        // If no height in event, do a single detection (debounced)
        if (portalHeaderDebounceTimeoutRef.current) {
          clearTimeout(portalHeaderDebounceTimeoutRef.current)
        }
        portalHeaderDebounceTimeoutRef.current = setTimeout(() => {
          // Only detect height, don't update visibility state
          const portalHeader = document.querySelector('[data-portal-header="true"]')
          if (portalHeader) {
            const rect = portalHeader.getBoundingClientRect()
            const newHeight = rect.height > 0 ? rect.height : 0
            if (newHeight !== portalHeaderHeightRef.current) {
              setPortalHeaderHeight(newHeight)
              portalHeaderHeightRef.current = newHeight
            }
          }
        }, 300) // Debounce to prevent rapid updates
      }
    }
    
    window.addEventListener('portal-header-visibility-change', handlePortalHeaderChange)
    
    // Periodically check portal header (in case DOM changes) - reduced frequency to prevent loops
    portalHeaderCheckIntervalRef.current = setInterval(detectPortalHeader, 5000) // Increased to 5 seconds
    
    return () => {
      if (portalHeaderDebounceTimeoutRef.current) {
        clearTimeout(portalHeaderDebounceTimeoutRef.current)
      }
      window.removeEventListener('portal-header-visibility-change', handlePortalHeaderChange)
      if (portalHeaderCheckIntervalRef.current) {
        clearInterval(portalHeaderCheckIntervalRef.current)
      }
    }
  }, [])
  
  // Listen for high contrast changes
  useEffect(() => {
    const handleHighContrastChange = (event) => {
      setHighContrast(event.detail?.enabled || false)
    }
    
    window.addEventListener('calendar-header-high-contrast-change', handleHighContrastChange)
    
    return () => {
      window.removeEventListener('calendar-header-high-contrast-change', handleHighContrastChange)
    }
  }, [])

  // Calendar header is always visible, scrolls behind portal header

  // Calculate top position: position calendar header below portal header if it exists
  // On PC, always account for portal header height to prevent overlap
  // On mobile, also account for page header height
  const calculateTopPosition = () => {
    if (isMobile) {
      // On mobile, use the tracked page header height (most reliable)
      if (mobilePageHeaderHeight > 0) {
        return mobilePageHeaderHeight
      }
      
      // Fallback: check for page header directly
      const pageHeader = document.querySelector('[data-portal-header="true"]') || 
                        document.querySelector('[class*="PageHeadline"]') ||
                        document.querySelector('[class*="page-headline"]')
      
      if (pageHeader) {
        const pageHeaderHeight = pageHeader.offsetHeight || pageHeader.getBoundingClientRect().height
        if (pageHeaderHeight > 0) {
          return pageHeaderHeight
        }
      }
      
      // Fallback to headerContainerStyles if provided
      return headerContainerStyles?.top || 0
    }
    
    // Priority 1: Use ref value if available (most reliable)
    if (portalHeaderHeightRef.current > 0) {
      return portalHeaderHeightRef.current
    }
    
    // Priority 2: Check portal header in DOM directly (for initial render)
    // Always get the actual height, even if header is temporarily hidden
    const portalHeader = document.querySelector('[data-portal-header="true"]')
    if (portalHeader) {
      // Use offsetHeight as it gives the actual element height regardless of visibility
      // This is important because getBoundingClientRect().height can be 0 when element is hidden
      const actualHeight = portalHeader.offsetHeight || portalHeader.getBoundingClientRect().height
      if (actualHeight > 0) {
        return actualHeight
      }
    }
    
    // If no portal header exists, start at the very top (0)
    return 0
  }

  // Track header height for margin calculation
  const [headerHeightForMargin, setHeaderHeightForMargin] = useState(0)
  
  // Track page header height on mobile for positioning
  const [mobilePageHeaderHeight, setMobilePageHeaderHeight] = useState(0)

  // On mobile the header is position:fixed (removed from flow), so the content beneath it would slide
  // up and be partly covered. Reserve its height with an in-flow spacer (rendered after it below).
  const [mobileReservedHeight, setMobileReservedHeight] = useState(0)
  useEffect(() => {
    if (!isMobile || collapsed) {
      setMobileReservedHeight(0)
      return undefined
    }
    const el = calendarHeaderRef.current
    if (!el) return undefined
    const measure = () => {
      const h = el.offsetHeight || el.getBoundingClientRect().height
      if (h > 0) setMobileReservedHeight(h)
    }
    measure()
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      ro.observe(el)
    }
    window.addEventListener('resize', measure)
    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isMobile, collapsed, hideDatePicker, selectedDate])

  // Update page header height on mobile
  useEffect(() => {
    if (!isMobile) return
    
    const updatePageHeaderHeight = () => {
      const pageHeader = document.querySelector('[data-portal-header="true"]') || 
                        document.querySelector('[class*="PageHeadline"]') ||
                        document.querySelector('[class*="page-headline"]')
      
      if (pageHeader) {
        const height = pageHeader.offsetHeight || pageHeader.getBoundingClientRect().height
        if (height > 0 && height !== mobilePageHeaderHeight) {
          setMobilePageHeaderHeight(height)
        }
      }
    }
    
    // Initial check
    updatePageHeaderHeight()
    
    // Update on resize
    window.addEventListener('resize', updatePageHeaderHeight)
    
    // Also check periodically in case header height changes
    const interval = setInterval(updatePageHeaderHeight, 1000)
    
    return () => {
      window.removeEventListener('resize', updatePageHeaderHeight)
      clearInterval(interval)
    }
  }, [isMobile, mobilePageHeaderHeight])
  
  // Update header height for margin calculation
  useEffect(() => {
    if (calendarHeaderRef.current && !isMobile) {
      const updateHeight = () => {
        const height = calendarHeaderRef.current.offsetHeight || calendarHeaderRef.current.getBoundingClientRect().height
        if (height > 0 && height !== headerHeightForMargin) {
          setHeaderHeightForMargin(height)
        }
      }
      updateHeight()
      // Update on resize
      window.addEventListener('resize', updateHeight)
      return () => window.removeEventListener('resize', updateHeight)
    }
  }, [isMenuVisible, isMobile, headerHeightForMargin])
  
  
  // Calendar header scrolls behind portal header
  const topPosition = calculateTopPosition()
  
  // Extract margin and position properties from headerContainerStyles to avoid conflicts
  // We need to exclude position, top, left, right, width, zIndex as these are controlled by this component
  // Note: 'width' is already declared from useScreenSize(), so we don't destructure it here
  const { 
    margin, 
    marginTop, 
    marginBottom, 
    marginLeft, 
    marginRight,
    position, // Exclude position - we control it
    top, // Exclude top - we control it
    left, // Exclude left - we control it
    right, // Exclude right - we control it
    width: _width, // Exclude width - we control it on mobile (renamed to avoid conflict with useScreenSize width)
    zIndex, // Exclude zIndex - we control it
    ...restHeaderStyles 
  } = headerContainerStyles || {}
  
  const finalHeaderStyles = {
    ...restHeaderStyles,
    // Add small padding-top for visual spacing
    paddingTop: isMobile ? '3px' : '12px',
    // On mobile, position fixed below page header with proper z-index
    // On PC, use sticky positioning starting at the top of main body (below page header)
    // IMPORTANT: These styles must come AFTER restHeaderStyles to override any conflicting values
    ...(isMobile ? {
      position: 'fixed',
      top: `${topPosition}px`,
      left: 0,
      right: 0,
      zIndex: 12, // Lower than page header (15) but above content
      width: '100%',
      // Use non-shorthand margin properties to avoid conflicts
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    } : { 
      position: 'sticky',
      top: `${topPosition}px`, // Start at the top of main body (right below page header)
      zIndex: 10, // Lower than portal header (15) so it scrolls behind
      // Use non-shorthand margin properties to avoid conflicts
      marginTop: 0, // Ensure no top margin pushes it down
      marginBottom: marginBottom || 0,
      marginLeft: marginLeft || 0,
      marginRight: marginRight || 0,
    }),
    // Always visible
    ...(isMobile ? {} : {
      opacity: 1,
      transform: 'translateY(0)',
      pointerEvents: 'auto',
      visibility: 'visible',
    }),
    // High Contrast border - light, professional color
    ...(highContrast ? {
      border: '2px solid rgba(67, 56, 202, 0.4)',
      boxShadow: '0 0 0 1px rgba(67, 56, 202, 0.2), 0 4px 16px rgba(67, 56, 202, 0.12)',
    } : {}),
  }
  
  // Removed excessive debug logging to prevent render loops

  // Add data attribute for easier debugging/selection
  // Removed excessive logging to prevent render loops
  
  // Track header height and dispatch events for spacer
  // Always maintain height in spacer to prevent layout shifts when header appears/disappears
  // Use ref to track previous height to avoid unnecessary dispatches
  const previousHeightRef = useRef(0)
  
  useEffect(() => {
    if (calendarHeaderRef.current && !isMobile) {
      const updateHeight = () => {
        if (calendarHeaderRef.current) {
          const height = calendarHeaderRef.current.offsetHeight || calendarHeaderRef.current.getBoundingClientRect().height
          // Always dispatch height (regardless of visibility) to maintain spacer
          // This prevents layout shifts when header appears/disappears
          if (height > 0 && height !== previousHeightRef.current) {
            previousHeightRef.current = height
            // Dispatch height change event for parent to use as spacer
            // Always send height, not dependent on visibility
            window.dispatchEvent(new CustomEvent('calendar-header-height-change', {
              detail: { height }
            }))
          }
        }
      }
      
      // Update height immediately and on visibility changes
      updateHeight()
      
      // Update on resize (debounced)
      let resizeTimeoutId
      const handleResize = () => {
        clearTimeout(resizeTimeoutId)
        resizeTimeoutId = setTimeout(updateHeight, 150)
      }
      
      window.addEventListener('resize', handleResize)
      return () => {
        clearTimeout(resizeTimeoutId)
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [isMenuVisible, isMobile])
  
  // No transform reset needed - header is always visible
  
  // Common Date Picker Component - used by both mentor and clinic assistant branches
  const commonDatePicker = (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'nowrap',
      margin: '0 auto',
      maxWidth: '100%',
      gap: isMobile ? 0.35 : 0.5,
    }}>
      <EDVCDateContainer
        compact={compactUI}
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          width: 'auto',
          maxWidth: '100%',
          gap: isMobile ? '4px' : undefined,
        }}
      >
        {/* PREVIOUS DAY BUTTON */}
        <DateNavButton 
          onClick={onPrevClick} 
          title="Previous Day"
          style={isMobile ? { flexShrink: 0, padding: '6px 8px', minWidth: '36px' } : { flexShrink: 0 }}
        >
          <ArrowLeftIcon sx={{ fontSize: 20 }} />
          {!isMobile && (
            <span>
              {isToday
                ? 'Yesterday'
                : dayjs(selectedDate, 'DD-MM-YYYY').subtract(1, 'days').format('D MMM')}
            </span>
          )}
        </DateNavButton>

        {/* DATE SELECTOR */}
        <GenericDisplayFlexColumn
          aic
          sx={{
            justifyContent: 'center',
            gap: 0.5,
            flex: 'unset',
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.75,
            flex: 'unset',
            minWidth: 0,
            flexShrink: 0,
          }}>
            <MomentDatePicker
              date={selectedDateDayjs}
              buttonTextOverride={
                isMobile
                  ? mobileDateButtonLabel
                  : `${selectedDate}, ${isToday ? 'Today' : selectedDateDayjs.format('ddd')}`
              }
              buttonProps={{
                variant: 'outlined',
                size: 'small',
                endIcon: <CalendarMonthIcon />,
                sx: {
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: 'rgba(67, 56, 202, 0.85)',
                  textTransform: 'none',
                  border: '1px solid rgba(67, 56, 202, 0.25)',
                  fontWeight: 600,
                  fontSize: isMobile ? '12px' : '13px',
                  padding: isMobile ? '6px 10px' : '6px 12px',
                  minHeight: isMobile ? '36px' : 'auto',
                  lineHeight: 1.2,
                  boxShadow: '0 1px 2px rgba(67, 56, 202, 0.05)',
                  transition: 'all 0.2s ease',
                  maxWidth: isMobile ? '150px' : 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': {
                    borderColor: 'rgba(67, 56, 202, 0.45)',
                    background: 'rgba(67, 56, 202, 0.08)',
                    color: 'rgba(67, 56, 202, 1)',
                    boxShadow: '0 2px 4px rgba(67, 56, 202, 0.12)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                },
              }}
              onChange={(nextValue) => setSelectedDate(nextValue?.format('DD-MM-YYYY'))}
              calendarProps={{
                minDetail: 'decade',
                showNeighboringMonth: false,
                locale: sessionStorage.getItem('languageCode') || 'en',
              }}
              clinicSchedules={clinicSchedules}
              selectedDate={selectedDate}
            />
            {!isToday && !isMobile && (
              <TodayButton 
                onClick={handleGoToToday} 
                title="Go to Today"
                style={{ flexShrink: 0 }}
              >
                <CalendarMonthIcon sx={{ fontSize: 20 }} />
                {!isMobile && <span>Today</span>}
              </TodayButton>
            )}
          </Box>
        </GenericDisplayFlexColumn>

        {/* NEXT DAY BUTTON */}
        <DateNavButton 
          onClick={onNextClick} 
          title="Next Day"
          style={isMobile ? { flexShrink: 0, padding: '6px 8px', minWidth: '36px' } : { flexShrink: 0 }}
        >
          {!isMobile && (
            <span>
              {isToday
                ? 'Tomorrow'
                : dayjs(selectedDate, 'DD-MM-YYYY').add(1, 'days').format('D MMM')}
            </span>
          )}
          <ArrowRightIcon sx={{ fontSize: 20 }} />
        </DateNavButton>
      </EDVCDateContainer>
    </Box>
  )

  // Chamber-drawer style date navigation for the slim mobile mentor header: a calendar icon that
  // opens the full picker, plus Yesterday / Today / Tomorrow segmented tabs (a 4th "picked" chip
  // appears when some other date is selected — tap it to jump back to today).
  const todayStr = dayjs().format('DD-MM-YYYY')
  const yesterdayStr = dayjs().subtract(1, 'day').format('DD-MM-YYYY')
  const tomorrowStr = dayjs().add(1, 'day').format('DD-MM-YYYY')
  const dayTabs = [
    { id: yesterdayStr, label: 'Yesterday' },
    { id: todayStr, label: 'Today' },
    { id: tomorrowStr, label: 'Tomorrow' },
  ]
  const isPickedDate = ![yesterdayStr, todayStr, tomorrowStr].includes(selectedDate)
  const dayTabBaseSx = {
    flex: 1,
    minWidth: 0,
    py: 0.6,
    borderRadius: '8px',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: '12.5px',
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    userSelect: 'none',
  }
  const dayTabActiveSx = {
    fontWeight: 700,
    color: '#6779e3',
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  }
  const dayTabIdleSx = { fontWeight: 600, color: 'rgba(31, 41, 55, 0.7)', background: 'transparent' }
  const mobileDateTabs = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', maxWidth: '100%' }}>
      <MomentDatePicker
        date={selectedDateDayjs}
        buttonTextOverride=" "
        buttonProps={{
          variant: 'outlined',
          size: 'small',
          endIcon: <CalendarMonthIcon sx={{ fontSize: 16 }} />,
          sx: {
            minWidth: '34px',
            width: '34px',
            height: '32px',
            p: 0,
            flexShrink: 0,
            color: '#6779e3',
            borderColor: 'rgba(103, 121, 227, 0.3)',
            backgroundColor: 'rgba(103, 121, 227, 0.08)',
            '& .MuiButton-endIcon': { m: 0 },
            '&:hover': { borderColor: 'rgba(103, 121, 227, 0.5)', backgroundColor: 'rgba(103, 121, 227, 0.16)' },
          },
        }}
        onChange={(nextValue) => setSelectedDate(nextValue?.format('DD-MM-YYYY'))}
        calendarProps={{
          minDetail: 'decade',
          showNeighboringMonth: false,
          locale: sessionStorage.getItem('languageCode') || 'en',
        }}
        clinicSchedules={clinicSchedules}
        selectedDate={selectedDate}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          gap: '4px',
          p: '3px',
          borderRadius: '9px',
          background: 'rgba(103, 121, 227, 0.08)',
        }}
      >
        {dayTabs.map((tab) => {
          const active = selectedDate === tab.id
          return (
            <Box
              key={tab.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedDate(tab.id)}
              sx={{ ...dayTabBaseSx, ...(active ? dayTabActiveSx : dayTabIdleSx) }}
            >
              {tab.label}
            </Box>
          )
        })}
        {isPickedDate && (
          <Box
            role="button"
            tabIndex={0}
            title="Tap to return to Today"
            onClick={handleGoToToday}
            sx={{ ...dayTabBaseSx, ...dayTabActiveSx }}
          >
            {selectedDateDayjs.format('D MMM')}
          </Box>
        )}
      </Box>
    </Box>
  )

  return (
    <>
    <FloatingHeaderContainerDayCal
      ref={calendarHeaderRef}
      style={collapsed ? { ...finalHeaderStyles, display: 'none' } : finalHeaderStyles}
      compact={compactUI}
      $slim={hideDatePicker}
      $flush={forMentor && isMobile}
      $collapsed={collapsed}
      data-calendar-header="true"
      data-menu-visible={isMenuVisible}
    >
      <div className="d-flex flex-column w-100">
        <div className="d-flex flex-direction-row justify-content-center">
          {/* left side component */}
          <div style={{ flexShrink: 0, flexGrow: 0 }}>{LeftSideExtraComponent}</div>
          <div
            className={`w-100 d-flex ${compactUI ? 'flex-row-reverse' : 'flex-column'}`}
            style={{ position: 'relative', padding: compactUI ? '0px' : isMobile ? '0px 8px 2px' : (!forMentor && !isMobile ? '8px 12px' : '6px 16px') }}
          >
            {/* Reorganized Layout for Clinic Assistant - Two Rows on Desktop */}
            {!forMentor ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: 1,
                  minHeight: '88px',
                }}
              >
                {/* Row 1: Practitioner Button | Date Picker Group */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: { xs: 0.75, sm: 1, md: 1.5 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    overflow: 'hidden',
                    minWidth: 0,
                    minHeight: { xs: 'auto', sm: '44px' },
                  }}
                >
                  {/* Left: Practitioner Button - Always visible, critical for clinic assistant flow */}
                  <Box sx={{ 
                    flexShrink: 0, 
                    minWidth: 0, 
                    maxWidth: { xs: '140px', sm: '180px', md: '220px' },
                    width: { xs: 'auto', sm: 'auto' }
                  }}>
                    {showPractitionerInfo && practitionerButtonProps && (
                      <SimplePractitionerButton
                        imageUrl={practitionerButtonProps.imageUrl}
                        managerId={practitionerButtonProps.managerId}
                        buttonText={practitionerButtonProps.buttonText}
                        onClick={onOpenPractitionerDrawer}
                        isActive={practitionerButtonProps.isActive}
                        variant="floating"
                        role={practitionerButtonProps.role}
                      />
                    )}
                  </Box>

                  {/* Center: Date Picker Group - Always visible */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Date Picker Group - All date-related actions on same line, never wrap, centered */}
                    {commonDatePicker}
                  </Box>
                </Box>

                {/* Row 2: View All Booked Slots | Patient Search | Filter Pills | 3-dots Menu */}
                {/* Hide on mobile when CalendarHeaderFloatingBar is visible to avoid duplicate buttons */}
                {!(isMobile && isFloatingBarVisible) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1,
                      minHeight: '40px',
                    }}
                  >
                    {/* Left: View All Booked Slots - Always visible on header strip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* View All Booked Slots - Always visible */}
                      {onOpenBookedSlotsDrawer && (
                        <Tooltip title="View All Booked Slots">
                          <IconButton
                            size="small"
                            onClick={onOpenBookedSlotsDrawer}
                            sx={{
                              color: 'rgba(67, 56, 202, 0.7)',
                              backgroundColor: 'rgba(67, 56, 202, 0.04)',
                              border: '1px solid rgba(67, 56, 202, 0.15)',
                              padding: '8px',
                              '&:hover': {
                                backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                borderColor: 'rgba(67, 56, 202, 0.3)',
                                color: 'rgba(67, 56, 202, 1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Patient Search - Visible on header card */}
                      {onOpenPatientSearch && (
                        <Tooltip title="Search / Add Patient">
                          {width < 900 ? (
                            // Icon-only when space is limited
                            <IconButton
                              size="small"
                              onClick={onOpenPatientSearch}
                              sx={{
                                color: '#6779e3',
                                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                                border: '1px solid rgba(103, 121, 227, 0.2)',
                                padding: '8px',
                                '&:hover': {
                                  backgroundColor: 'rgba(103, 121, 227, 0.2)',
                                  borderColor: 'rgba(103, 121, 227, 0.4)',
                                  color: '#6779e3',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <SearchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          ) : (
                            // Full button when space allows
                            <Button
                              size="small"
                              startIcon={<SearchIcon />}
                              onClick={onOpenPatientSearch}
                              sx={{
                                color: '#6779e3',
                                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                                border: '1px solid rgba(103, 121, 227, 0.2)',
                                padding: '6px 12px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px',
                                '&:hover': {
                                  backgroundColor: 'rgba(103, 121, 227, 0.2)',
                                  borderColor: 'rgba(103, 121, 227, 0.4)',
                                },
                                transition: 'all 0.2s ease',
                                '& .MuiButton-startIcon': {
                                  marginRight: '6px',
                                  '& > *:nth-of-type(1)': {
                                    fontSize: '16px',
                                  },
                                },
                              }}
                            >
                              Patient Search
                            </Button>
                          )}
                        </Tooltip>
                      )}

                      {/* New patient booking - Appointments tab only */}
                      {showNewBookingButton && onOpenNewBooking && (
                        <NewBookingHeaderButton
                          onClick={onOpenNewBooking}
                          compact={width < 900}
                        />
                      )}
                    </Box>

                    {/* Center: Filter Pills - All Patients, In Person, Video Call */}
                    {selectedFilterType !== undefined && setSelectedFilterType && bookedSlotsInDay && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1,
                          gap: 0.5,
                          flexWrap: 'nowrap',
                          overflow: 'hidden',
                          minWidth: 0,
                          maxWidth: '100%',
                          px: 1,
                        }}
                      >
                        <EDVCSessionsCompletedDetailsComponent
                          stacked={forMentor}
                          noMargins={true}
                          compactUI={true}
                          bookedSlotsInDay={bookedSlotsInDay}
                          cancelledCount={cancelledCount}
                          showCancelledOnly={showCancelledOnly}
                          setShowCancelledOnly={setShowCancelledOnly}
                          showCancelledChip={showCancelledChip}
                          includeReportRelatedData={includeReportRelatedData}
                          setIncludeReportRelatedData={setIncludeReportRelatedData}
                          hideToggleSwitchForReportRelatedData={true}
                          selectedFilterType={selectedFilterType}
                          setSelectedFilterType={setSelectedFilterType}
                          onOpenBookedSlotsDrawer={onOpenBookedSlotsDrawer}
                        />
                      </Box>
                    )}

                    {/* Right: 3-dots Menu for other actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={handleCaActionsMenuOpen}
                          sx={{
                            color: 'rgba(67, 56, 202, 0.7)',
                            backgroundColor: 'rgba(67, 56, 202, 0.04)',
                            border: '1px solid rgba(67, 56, 202, 0.15)',
                            padding: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              borderColor: 'rgba(67, 56, 202, 0.3)',
                              color: 'rgba(67, 56, 202, 1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {/* Menu and Popover for additional actions */}
                <>
                  {/* Menu for additional actions */}
                  <Menu
                    anchorEl={caActionsMenuAnchor}
                    open={Boolean(caActionsMenuAnchor)}
                    onClose={(e, reason) => {
                      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        handleCaActionsMenuClose()
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        minWidth: 220,
                        mt: 1,
                        boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
                        border: '1px solid rgba(67, 56, 202, 0.1)',
                        borderRadius: '12px',
                      },
                    }}
                    MenuListProps={{
                      onClick: (e) => e.stopPropagation(),
                      onMouseDown: (e) => e.stopPropagation(),
                      onWheel: (e) => e.stopPropagation(),
                      onScroll: (e) => e.stopPropagation(),
                      onTouchMove: (e) => e.stopPropagation(),
                    }}
                  >
                    {/* Refresh Bookings */}
                    {!hideRefreshButton && (
                      <MenuItem
                        onClick={() => {
                          getAndSetBookedSlotsOfSelectedDate()
                          handleCaActionsMenuClose()
                        }}
                        disabled={bookedSlotsInDayLoading}
                        sx={{
                          pointerEvents: 'auto',
                          userSelect: 'none',
                          py: 1.5,
                          '&.Mui-disabled': {
                            opacity: 0.6,
                          },
                        }}
                      >
                        {bookedSlotsInDayLoading ? (
                          <CircularProgress size={16} sx={{ mr: 1.5, color: 'rgba(67, 56, 202, 0.7)' }} />
                        ) : (
                          <StyledEventsRefreshIcon sx={{ mr: 1.5, fontSize: 20 }} />
                        )}
                        Refresh Bookings
                      </MenuItem>
                    )}

                    {/* Slot Duration */}
                    {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            // Store the menu's anchor (3-dots button) before closing the menu
                            const menuAnchor = caActionsMenuAnchor
                            
                            // Close the menu first
                            handleCaActionsMenuClose()
                            
                            // Use setTimeout to ensure menu is closed before opening popover
                            // Use the menu's anchor (3-dots button) as the popover anchor
                            setTimeout(() => {
                              if (menuAnchor && typeof document !== 'undefined' && document.body.contains(menuAnchor)) {
                                setSlotDurationAnchorEl(menuAnchor)
                                // Reset local state to current value when opening
                                if (eachSlotDurationInMins !== undefined) {
                                  setLocalSlotDuration(eachSlotDurationInMins)
                                }
                              } else {
                                // Fallback: create a synthetic anchor at a reasonable position
                                const syntheticAnchor = document.createElement('div')
                                syntheticAnchor.style.position = 'fixed'
                                syntheticAnchor.style.top = '80px'
                                syntheticAnchor.style.right = '20px'
                                syntheticAnchor.style.width = '1px'
                                syntheticAnchor.style.height = '1px'
                                syntheticAnchor.style.zIndex = '9999'
                                syntheticAnchor.style.pointerEvents = 'none'
                                if (typeof document !== 'undefined' && document.body) {
                                  document.body.appendChild(syntheticAnchor)
                                  syntheticAnchorRef.current = syntheticAnchor
                                  setSlotDurationAnchorEl(syntheticAnchor)
                                  // Reset local state to current value when opening
                                  if (eachSlotDurationInMins !== undefined) {
                                    setLocalSlotDuration(eachSlotDurationInMins)
                                  }
                                }
                              }
                            }, 100)
                          }}
                          sx={{
                            pointerEvents: 'auto',
                            userSelect: 'none',
                            py: 1.5,
                          }}
                        >
                          <HeightIcon sx={{ mr: 1.5, fontSize: 20 }} />
                          Adjust Slot Duration
                        </MenuItem>
                      </>
                    )}

                    {/* Sticky Header Toggle */}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      onClick={() => {
                        setShowCalendarHeaderAfterScroll(!showCalendarHeaderAfterScroll)
                        handleCaActionsMenuClose()
                      }}
                      sx={{
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        py: 1.5,
                      }}
                    >
                      {showCalendarHeaderAfterScroll ? (
                        <PushPinIcon sx={{ mr: 1.5, fontSize: 20 }} />
                      ) : (
                        <PushPinOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} />
                      )}
                      {showCalendarHeaderAfterScroll ? 'Hide' : 'Show'} Date Picker After Scroll
                    </MenuItem>
                  </Menu>

                  {/* Slot Duration Popover for CA Portal */}
                  {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                    <Popover
                      open={slotDurationPopoverOpen && !!slotDurationAnchorEl && (typeof document !== 'undefined' && document.body.contains(slotDurationAnchorEl))}
                      anchorEl={slotDurationAnchorEl}
                      onClose={handleSlotDurationClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      disableRestoreFocus
                      disableAutoFocus
                      disableEnforceFocus
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      PaperProps={{
                        onClick: (e) => e.stopPropagation(),
                        onMouseDown: (e) => e.stopPropagation(),
                        sx: {
                          padding: 0,
                          minWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                          maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                          borderRadius: '16px',
                          boxShadow: '0 8px 32px rgba(67, 56, 202, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                          border: '1px solid rgba(67, 56, 202, 0.15)',
                          overflow: 'visible',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%)',
                          backdropFilter: 'blur(20px)',
                          position: 'relative',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 50%, rgba(99, 102, 241, 0.95) 100%)',
                          padding: { xs: '16px', sm: '20px' },
                          color: 'white',
                          overflow: 'visible',
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          marginBottom: '4px',
                          flexWrap: 'nowrap',
                          minWidth: 0,
                        }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '16px', sm: '18px' },
                              letterSpacing: '0.3px',
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            Slot Duration
                          </Typography>
                          <Tooltip title="Debug info about slot duration calculation">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDebugInfo(!showDebugInfo)
                              }}
                              sx={{
                                padding: { xs: '6px', sm: '4px' },
                                minWidth: { xs: '32px', sm: 'auto' },
                                minHeight: { xs: '32px', sm: 'auto' },
                                flexShrink: 0,
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  color: 'rgba(255, 255, 255, 0.95)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                },
                              }}
                            >
                              <InfoOutlinedIcon sx={{ 
                                fontSize: { xs: '18px', sm: '16px' },
                                display: 'block',
                              }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            opacity: 0.95,
                            fontWeight: 500,
                          }}
                        >
                          Adjust how many minutes each calendar slot represents
                        </Typography>
                        {showDebugInfo && (() => {
                          const debugInfo = getSlotDurationDebugInfo()
                          if (!debugInfo) return null
                          return (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                color: 'rgba(255, 255, 255, 0.95)',
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                Debug Info:
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                <div><strong>Current Duration:</strong> {eachSlotDurationInMins} mins</div>
                                <div><strong>System Calculated:</strong> {debugInfo.calculatedDuration} mins</div>
                                <div><strong>Status:</strong> {isSlotDurationManuallySet ? '🔒 User Set' : '🔄 System Determined'}</div>
                                {isSlotDurationManuallySet && (
                                  <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                                    Use &quot;Reset&quot; button to return to system-determined value
                                  </div>
                                )}
                              </Box>
                            </Box>
                          )
                        })()}
                      </Box>
                      <Box sx={{ padding: { xs: '20px', sm: '24px' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%)',
                            borderRadius: '12px',
                            padding: { xs: '16px', sm: '20px' },
                            border: '1px solid rgba(67, 56, 202, 0.12)',
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: 'rgba(67, 56, 202, 1)',
                              fontSize: { xs: '24px', sm: '28px' },
                              textAlign: 'center',
                              marginBottom: '8px',
                              letterSpacing: '-0.5px',
                            }}
                          >
                            {formatDuration(localSlotDuration)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              textAlign: 'center',
                              color: 'rgba(67, 56, 202, 0.7)',
                              fontSize: { xs: '11px', sm: '12px' },
                              fontWeight: 500,
                            }}
                          >
                            Current duration per slot
                          </Typography>
                        </Box>
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Slider
                            value={localSlotDuration}
                            onChange={handleSlotDurationChange}
                            min={MIN_SLOT_DURATION}
                            max={MAX_SLOT_DURATION}
                            step={5}
                            marks={[
                              { value: 5, label: '5 min' },
                              { value: 15, label: '15 min' },
                              { value: 30, label: '30 min' },
                              { value: 60, label: '1h' },
                              { value: 90, label: '90m' },
                              { value: 120, label: '2h' },
                            ]}
                            valueLabelDisplay="on"
                            slotProps={{
                              valueLabel: {
                                sx: {
                                  '& .MuiSlider-valueLabel': {
                                    backgroundColor: 'rgba(67, 56, 202, 0.95)',
                                    fontSize: { xs: '12px', sm: '13px' },
                                    fontWeight: 600,
                                    padding: { xs: '4px 8px', sm: '6px 10px' },
                                    borderRadius: '8px',
                                    '&::before': {
                                      display: 'none',
                                    },
                                  },
                                },
                              },
                              thumb: {
                                sx: {
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                  },
                                  '&:active': {
                                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                  },
                                },
                              },
                            }}
                            sx={{
                              color: 'rgba(67, 56, 202, 0.9)',
                              height: { xs: 6, sm: 8 },
                              '& .MuiSlider-thumb': {
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 },
                                backgroundColor: 'rgba(67, 56, 202, 1)',
                                border: '3px solid white',
                                boxShadow: '0 2px 8px rgba(67, 56, 202, 0.4), 0 0 0 4px rgba(67, 56, 202, 0.1)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                  transform: 'scale(1.1)',
                                },
                                '&:active': {
                                  transform: 'scale(1.15)',
                                },
                                '& .MuiSlider-valueLabel': {
                                  position: 'absolute !important',
                                  top: { xs: '-42px !important', sm: '-48px !important' },
                                  left: '50% !important',
                                  transform: 'translateX(-50%) !important',
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                },
                              },
                              '& .MuiSlider-valueLabel': {
                                position: 'absolute !important',
                                top: { xs: '-42px !important', sm: '-48px !important' },
                                left: '50% !important',
                                transform: 'translateX(-50%) !important',
                                pointerEvents: 'none',
                                zIndex: 1,
                              },
                              '& .MuiSlider-track': {
                                backgroundColor: 'rgba(67, 56, 202, 0.9)',
                                height: { xs: 6, sm: 8 },
                                border: 'none',
                              },
                              '& .MuiSlider-rail': {
                                backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                height: { xs: 6, sm: 8 },
                              },
                              '& .MuiSlider-mark': {
                                backgroundColor: 'rgba(67, 56, 202, 0.3)',
                                width: { xs: 4, sm: 6 },
                                height: { xs: 4, sm: 6 },
                                borderRadius: '50%',
                              },
                              '& .MuiSlider-markLabel': {
                                fontSize: { xs: '10px', sm: '11px' },
                                fontWeight: 600,
                                color: 'rgba(67, 56, 202, 0.7)',
                                marginTop: { xs: '8px', sm: '10px' },
                              },
                            }}
                            valueLabelFormat={(value) => formatDuration(value)}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: { xs: 2, sm: 0 },
                            paddingTop: { xs: '8px', sm: 0 },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(67, 56, 202, 0.65)',
                              fontSize: { xs: '11px', sm: '12px' },
                              fontWeight: 500,
                              lineHeight: 1.5,
                            }}
                          >
                            Range: {formatDuration(MIN_SLOT_DURATION)} to {formatDuration(MAX_SLOT_DURATION)}
                          </Typography>
                          <Button
                            size="medium"
                            startIcon={<RestartAltIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                            onClick={handleResetSlotDuration}
                            disabled={!isSlotDurationManuallySet && !initialSlotDuration}
                            title={isSlotDurationManuallySet ? "Reset to system-determined value" : "No manual override to reset"}
                            sx={{
                              textTransform: 'none',
                              fontSize: { xs: '13px', sm: '14px' },
                              fontWeight: 600,
                              padding: { xs: '8px 16px', sm: '10px 20px' },
                              color: 'rgba(67, 56, 202, 0.9)',
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              border: '2px solid rgba(67, 56, 202, 0.2)',
                              borderRadius: '10px',
                              minWidth: { xs: '100%', sm: 'auto' },
                              '&:hover': {
                                backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                borderColor: 'rgba(67, 56, 202, 0.35)',
                                color: 'rgba(67, 56, 202, 1)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                              },
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          >
                            {isSlotDurationManuallySet ? 'Reset to System' : 'Reset'}
                          </Button>
                          <Button
                            size="medium"
                            variant="contained"
                            startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                            onClick={handleApplySlotDuration}
                            sx={{
                              textTransform: 'none',
                              fontSize: { xs: '13px', sm: '14px' },
                              fontWeight: 600,
                              padding: { xs: '8px 20px', sm: '10px 24px' },
                              background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                              color: 'white',
                              boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                              minWidth: { xs: '100%', sm: 'auto' },
                              '&:hover': {
                                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
                                boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    </Popover>
                  )}
                </>
              </Box>
            ) : (
              /* Original Layout for Mentor Flow or Mobile */
              <>
                {/* Row 1: Date Picker Group - Always on same line, even on mobile */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    mb: isViewingCalendarRelatedTab && isMobile ? 0 : isViewingCalendarRelatedTab ? 0.75 : 0,
                    flexWrap: 'nowrap',
                    minHeight: 'auto',
                  }}
                >
                  {/* Hide on mobile when CalendarHeaderFloatingBar is visible to avoid duplicate buttons */}
                  {onOpenBookedSlotsDrawer && !(isMobile && isFloatingBarVisible) && (
                    <Tooltip title="View All Booked Slots">
                      <IconButton
                        size="small"
                        onClick={onOpenBookedSlotsDrawer}
                        sx={{
                          color: 'rgba(67, 56, 202, 0.7)',
                          backgroundColor: 'rgba(67, 56, 202, 0.04)',
                          border: '1px solid rgba(67, 56, 202, 0.15)',
                          padding: '6px',
                          '&:hover': {
                            backgroundColor: 'rgba(67, 56, 202, 0.08)',
                            borderColor: 'rgba(67, 56, 202, 0.3)',
                            color: 'rgba(67, 56, 202, 1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Refresh button removed from here - it's now only in the 3-dots menu for CA desktop flow */}
                  {/* Patient Search Button - Hide on mobile when CalendarHeaderFloatingBar is visible */}
                  {onOpenPatientSearch && !(isMobile && isFloatingBarVisible) && (
                    isMobile ? (
                      <Tooltip title="Search / Add Patient">
                        <IconButton
                          size="small"
                          onClick={onOpenPatientSearch}
                          sx={{
                            color: '#6779e3',
                            backgroundColor: 'rgba(103, 121, 227, 0.1)',
                            border: '1px solid rgba(103, 121, 227, 0.2)',
                            padding: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(103, 121, 227, 0.2)',
                              borderColor: 'rgba(103, 121, 227, 0.4)',
                              color: '#6779e3',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <SearchIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={onOpenPatientSearch}
                        sx={{
                          backgroundColor: '#6779e3',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '13px',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          textTransform: 'none',
                          boxShadow: '0 2px 8px rgba(103, 121, 227, 0.25)',
                          border: '1px solid rgba(103, 121, 227, 0.3)',
                          minWidth: '140px',
                          '&:hover': {
                            backgroundColor: '#7450aa',
                            boxShadow: '0 4px 12px rgba(116, 80, 170, 0.35)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                          transition: 'all 0.2s ease',
                          '& .MuiButton-startIcon': {
                            marginRight: '6px',
                            '& > *:nth-of-type(1)': {
                              fontSize: '18px',
                            },
                          },
                        }}
                      >
                        Patient Search
                      </Button>
                    )
                  )}

                  {/* New patient booking - Appointments tab only */}
                  {showNewBookingButton && onOpenNewBooking && !(isMobile && isFloatingBarVisible) && (
                    <NewBookingHeaderButton
                      onClick={onOpenNewBooking}
                      compact={isMobile}
                    />
                  )}

                  {/* Date Picker Group - All date-related actions on same line, never wrap, centered.
                      Hidden when the host page shows the date picker in its own main header.
                      On mobile mentor flows this becomes the chamber-style calendar-icon + Yesterday/
                      Today/Tomorrow tabs; desktop and clinic-assistant keep the prev/next picker. */}
                  {!hideDatePicker && (forMentor && isMobile ? mobileDateTabs : commonDatePicker)}
                  
                  {/* Sticky Header Button removed - now only available in 3-dots menu */}
                  {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                    <>
                      <Popover
                        open={slotDurationPopoverOpen && !!slotDurationAnchorEl && (typeof document !== 'undefined' && document.body.contains(slotDurationAnchorEl))}
                        anchorEl={slotDurationAnchorEl}
                        onClose={handleSlotDurationClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        disableRestoreFocus
                        disableAutoFocus
                        disableEnforceFocus
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        PaperProps={{
                          onClick: (e) => e.stopPropagation(),
                          onMouseDown: (e) => e.stopPropagation(),
                          sx: {
                            padding: 0,
                            minWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(67, 56, 202, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(67, 56, 202, 0.15)',
                            overflow: 'visible', // Changed from 'hidden' to 'visible' to ensure button is not clipped
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%)',
                            backdropFilter: 'blur(20px)',
                            position: 'relative', // Ensure stable positioning
                          },
                        }}
                      >
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 50%, rgba(99, 102, 241, 0.95) 100%)',
                            padding: { xs: '16px', sm: '20px' },
                            color: 'white',
                            overflow: 'visible', // Ensure content is not clipped
                            position: 'relative',
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            marginBottom: '4px',
                            flexWrap: 'nowrap',
                            minWidth: 0, // Prevent flex items from shrinking below content
                          }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                fontSize: { xs: '16px', sm: '18px' },
                                letterSpacing: '0.3px',
                                flexShrink: 1,
                                minWidth: 0,
                              }}
                            >
                              Slot Duration
                            </Typography>
                            <Tooltip title="Debug info about slot duration calculation">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setShowDebugInfo(!showDebugInfo)
                                }}
                                sx={{
                                  padding: { xs: '6px', sm: '4px' },
                                  minWidth: { xs: '32px', sm: 'auto' },
                                  minHeight: { xs: '32px', sm: 'auto' },
                                  flexShrink: 0, // Prevent button from shrinking
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  '&:hover': {
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                  },
                                }}
                              >
                                <InfoOutlinedIcon sx={{ 
                                  fontSize: { xs: '18px', sm: '16px' },
                                  display: 'block', // Ensure icon is always visible
                                }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: '13px', sm: '14px' },
                              opacity: 0.95,
                              fontWeight: 500,
                            }}
                          >
                            Adjust how many minutes each calendar slot represents
                          </Typography>
                          {showDebugInfo && (() => {
                            const debugInfo = getSlotDurationDebugInfo()
                            if (!debugInfo) return null
                            return (
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  fontSize: '11px',
                                  fontFamily: 'monospace',
                                  color: 'rgba(255, 255, 255, 0.95)',
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                  Debug Info:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}>
                                  <div><strong>Current Duration:</strong> {eachSlotDurationInMins} mins</div>
                                  <div><strong>System Calculated:</strong> {debugInfo.calculatedDuration} mins</div>
                                  <div><strong>Status:</strong> {isSlotDurationManuallySet ? '🔒 User Set' : '🔄 System Determined'}</div>
                                  {isSlotDurationManuallySet && (
                                    <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                                      Use &quot;Reset&quot; button to return to system-determined value
                                    </div>
                                  )}
                                  
                                  {debugInfo.selectedOption && (
                                    <div style={{ marginTop: '8px' }}>
                                      <div><strong>Check-in Option:</strong> {debugInfo.selectedOption.title || debugInfo.selectedOption.id}</div>
                                      {debugInfo.detectedCheckInOptionType && (
                                        <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px' }}>
                                          <strong>Detected Type:</strong> {debugInfo.detectedCheckInOptionType}
                                          {debugInfo.detectedCheckInOptionType === 'screening-only' && ' (screening events)'}
                                          {debugInfo.detectedCheckInOptionType === 'therapy-only' && ' (therapy + assessment events)'}
                                          {debugInfo.detectedCheckInOptionType === 'screening-and-therapy' && ' (screening + therapy + assessment events)'}
                                        </div>
                                      )}
                                      {debugInfo.selectedOption.eventTypeIds && (
                                        <div style={{ fontSize: '10px', opacity: 0.8 }}>
                                          Maps to {debugInfo.selectedOption.eventTypeIds.length} event type(s) via eventTypeIds
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {debugInfo.eventTypeUsed && (
                                    <>
                                      <div style={{ marginTop: '8px' }}>
                                        <div><strong>Event Used:</strong> {debugInfo.eventTypeUsed.title || debugInfo.eventTypeUsed.uid}</div>
                                        <div><strong>Event Type:</strong> {debugInfo.eventTypeUsed.type}</div>
                                        <div><strong>Duration:</strong> {debugInfo.eventTypeUsed.durationInMins} mins</div>
                                        <div><strong>Event UID:</strong> {debugInfo.eventTypeUsed.uid}</div>
                                      </div>
                                    </>
                                  )}
                                  
                                  {debugInfo.checkInEventOptionId && (
                                    <div style={{ marginTop: '8px' }}>
                                      <div><strong>Check-in Event Option ID:</strong> {debugInfo.checkInEventOptionId}</div>
                                      <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px', marginLeft: '8px' }}>
                                        <strong>Event Types:</strong> {
                                          debugInfo.detectedCheckInOptionType === 'screening-only' 
                                            ? 'Screening' 
                                            : debugInfo.detectedCheckInOptionType === 'therapy-only'
                                            ? 'Therapy Session + Assessment'
                                            : debugInfo.detectedCheckInOptionType === 'screening-and-therapy'
                                            ? 'Screening + Therapy Session + Assessment'
                                            : debugInfo.matchingEvents.length > 0
                                            ? (() => {
                                                const types = [...new Set(debugInfo.matchingEvents.map(e => e.type))]
                                                return types.join(', ')
                                              })()
                                            : 'Not detected'
                                        }
                                      </div>
                                      {debugInfo.matchingEvents.length > 0 && (
                                        <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '4px', marginLeft: '8px' }}>
                                          <strong>Matching Events:</strong> {debugInfo.matchingEvents.length} found
                                          <div style={{ marginLeft: '8px', marginTop: '2px' }}>
                                            {debugInfo.matchingEvents.map((event, idx) => (
                                              <div key={idx} style={{ fontSize: '9px' }}>
                                                • {event.title || event.uid} ({event.type}) - {event.durationInMins || 'N/A'} mins
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
              {debugInfo.clinicId && (
                <div><strong>Clinic ID:</strong> {debugInfo.clinicId}</div>
              )}

              {debugInfo.roomDataEventTypes && debugInfo.roomDataEventTypes.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div><strong>Room Data Event Types:</strong> {debugInfo.roomDataEventTypes.length} event type(s) from room configuration</div>
                </div>
              )}

              {/* Slot sizes by event type */}
                                  {debugInfo.slotSizesByType && Object.keys(debugInfo.slotSizesByType).length > 0 && (
                                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        Slot Sizes by Event Type:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {Object.entries(debugInfo.slotSizesByType).map(([type, data]) => (
                                          <Box key={type} sx={{ pl: 1, borderLeft: '2px solid rgba(255, 255, 255, 0.3)' }}>
                                            <div><strong>{type}:</strong></div>
                                            {data.min !== null && data.max !== null ? (
                                              <div style={{ fontSize: '10px', marginLeft: '8px' }}>
                                                {data.min === data.max 
                                                  ? `${data.min} mins` 
                                                  : `${data.min}-${data.max} mins (${data.events.length} event${data.events.length !== 1 ? 's' : ''})`
                                                }
                                              </div>
                                            ) : (
                                              <div style={{ fontSize: '10px', marginLeft: '8px', opacity: 0.7 }}>
                                                Default: {data.default || 'N/A'} mins ({data.events.length} event{data.events.length !== 1 ? 's' : ''})
                                              </div>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            )
                          })()}
                        </Box>
                        <Box sx={{ padding: { xs: '20px', sm: '24px' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <Box
                            sx={{
                              background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%)',
                              borderRadius: '12px',
                              padding: { xs: '16px', sm: '20px' },
                              border: '1px solid rgba(67, 56, 202, 0.12)',
                            }}
                          >
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: 'rgba(67, 56, 202, 1)',
                                fontSize: { xs: '24px', sm: '28px' },
                                textAlign: 'center',
                                marginBottom: '8px',
                                letterSpacing: '-0.5px',
                              }}
                            >
                              {formatDuration(localSlotDuration)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                textAlign: 'center',
                                color: 'rgba(67, 56, 202, 0.7)',
                                fontSize: { xs: '11px', sm: '12px' },
                                fontWeight: 500,
                              }}
                            >
                              Current duration per slot
                            </Typography>
                          </Box>
                          <Box
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <Slider
                              value={localSlotDuration}
                              onChange={handleSlotDurationChange}
                              min={MIN_SLOT_DURATION}
                              max={MAX_SLOT_DURATION}
                              step={5}
                              marks={[
                                { value: 5, label: '5 min' },
                                { value: 15, label: '15 min' },
                                { value: 30, label: '30 min' },
                                { value: 60, label: '1h' },
                                { value: 90, label: '90m' },
                                { value: 120, label: '2h' },
                              ]}
                              valueLabelDisplay="on"
                              slotProps={{
                                valueLabel: {
                                  sx: {
                                    '& .MuiSlider-valueLabel': {
                                      backgroundColor: 'rgba(67, 56, 202, 0.95)',
                                      fontSize: { xs: '12px', sm: '13px' },
                                      fontWeight: 600,
                                      padding: { xs: '4px 8px', sm: '6px 10px' },
                                      borderRadius: '8px',
                                      '&::before': {
                                        display: 'none',
                                      },
                                    },
                                  },
                                },
                                thumb: {
                                  sx: {
                                    '&:hover': {
                                      boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                    },
                                    '&:active': {
                                      boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                    },
                                  },
                                },
                              }}
                              sx={{
                                color: 'rgba(67, 56, 202, 0.9)',
                                height: { xs: 6, sm: 8 },
                                '& .MuiSlider-thumb': {
                                  width: { xs: 20, sm: 24 },
                                  height: { xs: 20, sm: 24 },
                                  backgroundColor: 'rgba(67, 56, 202, 1)',
                                  border: '3px solid white',
                                  boxShadow: '0 2px 8px rgba(67, 56, 202, 0.4), 0 0 0 4px rgba(67, 56, 202, 0.1)',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                    transform: 'scale(1.1)',
                                  },
                                  '&:active': {
                                    transform: 'scale(1.15)',
                                  },
                                  '& .MuiSlider-valueLabel': {
                                    position: 'absolute !important',
                                    top: { xs: '-42px !important', sm: '-48px !important' },
                                    left: '50% !important',
                                    transform: 'translateX(-50%) !important',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                  },
                                },
                                '& .MuiSlider-valueLabel': {
                                  position: 'absolute !important',
                                  top: { xs: '-42px !important', sm: '-48px !important' },
                                  left: '50% !important',
                                  transform: 'translateX(-50%) !important',
                                  pointerEvents: 'none',
                                  zIndex: 1,
                                },
                                '& .MuiSlider-track': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.9)',
                                  height: { xs: 6, sm: 8 },
                                  border: 'none',
                                },
                                '& .MuiSlider-rail': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                  height: { xs: 6, sm: 8 },
                                },
                                '& .MuiSlider-mark': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.3)',
                                  width: { xs: 4, sm: 6 },
                                  height: { xs: 4, sm: 6 },
                                  borderRadius: '50%',
                                },
                                '& .MuiSlider-markLabel': {
                                  fontSize: { xs: '10px', sm: '11px' },
                                  fontWeight: 600,
                                  color: 'rgba(67, 56, 202, 0.7)',
                                  marginTop: { xs: '8px', sm: '10px' },
                                },
                              }}
                              valueLabelFormat={(value) => formatDuration(value)}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              gap: { xs: 2, sm: 0 },
                              paddingTop: { xs: '8px', sm: 0 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.65)',
                                fontSize: { xs: '11px', sm: '12px' },
                                fontWeight: 500,
                                lineHeight: 1.5,
                              }}
                            >
                              Range: {formatDuration(MIN_SLOT_DURATION)} to {formatDuration(MAX_SLOT_DURATION)}
                            </Typography>
                            <Button
                              size="medium"
                              startIcon={<RestartAltIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                              onClick={handleResetSlotDuration}
                              disabled={!isSlotDurationManuallySet && !initialSlotDuration}
                              title={isSlotDurationManuallySet ? "Reset to system-determined value" : "No manual override to reset"}
                              sx={{
                                textTransform: 'none',
                                fontSize: { xs: '13px', sm: '14px' },
                                fontWeight: 600,
                                padding: { xs: '8px 16px', sm: '10px 20px' },
                                color: 'rgba(67, 56, 202, 0.9)',
                                backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                border: '2px solid rgba(67, 56, 202, 0.2)',
                                borderRadius: '10px',
                                minWidth: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                  borderColor: 'rgba(67, 56, 202, 0.35)',
                                  color: 'rgba(67, 56, 202, 1)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                },
                                '&.Mui-disabled': {
                                  color: 'rgba(0, 0, 0, 0.26)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  borderColor: 'rgba(0, 0, 0, 0.12)',
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            >
                              {isSlotDurationManuallySet ? 'Reset to System' : 'Reset'}
                            </Button>
                            <Button
                              size="medium"
                              variant="contained"
                              startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                              onClick={handleApplySlotDuration}
                              sx={{
                                textTransform: 'none',
                                fontSize: { xs: '13px', sm: '14px' },
                                fontWeight: 600,
                                padding: { xs: '8px 20px', sm: '10px 24px' },
                                background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                                minWidth: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
                                  boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
                                  transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              Apply
                            </Button>
                          </Box>
                        </Box>
                      </Popover>
                    </>
                  )}
                  {!hideToggleSwitchForReportRelatedData && !forMentor && (
                    <Tooltip title={includeReportRelatedData ? 'Hide Reports Data' : 'Show Reports Data'}>
                      <IconButton
                        size="small"
                        onClick={() => setIncludeReportRelatedData((prev) => !prev)}
                        sx={{
                          color: includeReportRelatedData 
                            ? 'rgba(67, 56, 202, 1)' 
                            : 'rgba(67, 56, 202, 0.6)',
                          backgroundColor: includeReportRelatedData
                            ? 'rgba(67, 56, 202, 0.12)'
                            : 'rgba(67, 56, 202, 0.04)',
                          border: includeReportRelatedData
                            ? '1px solid rgba(67, 56, 202, 0.3)'
                            : '1px solid rgba(67, 56, 202, 0.15)',
                          padding: '6px',
                          '&:hover': {
                            backgroundColor: includeReportRelatedData
                              ? 'rgba(67, 56, 202, 0.16)'
                              : 'rgba(67, 56, 202, 0.08)',
                            borderColor: 'rgba(67, 56, 202, 0.4)',
                            color: 'rgba(67, 56, 202, 1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {includeReportRelatedData ? (
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <DescriptionIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {/* 3-dots Menu for Mentor Flow — suppressed when the host page opts out. */}
                  {forMentor && !hideOverflowMenu && (
                    <>
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={handleCaActionsMenuOpen}
                          sx={{
                            color: 'rgba(67, 56, 202, 0.7)',
                            backgroundColor: 'rgba(67, 56, 202, 0.04)',
                            border: '1px solid rgba(67, 56, 202, 0.15)',
                            padding: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              borderColor: 'rgba(67, 56, 202, 0.3)',
                              color: 'rgba(67, 56, 202, 1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>

                      {/* Menu for additional actions */}
                      <Menu
                        anchorEl={caActionsMenuAnchor}
                        open={Boolean(caActionsMenuAnchor)}
                        onClose={(e, reason) => {
                          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                            handleCaActionsMenuClose()
                          }
                        }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        PaperProps={{
                          sx: {
                            minWidth: 220,
                            mt: 1,
                            boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
                            border: '1px solid rgba(67, 56, 202, 0.1)',
                            borderRadius: '12px',
                          },
                        }}
                        MenuListProps={{
                          onClick: (e) => e.stopPropagation(),
                          onMouseDown: (e) => e.stopPropagation(),
                          onWheel: (e) => e.stopPropagation(),
                          onScroll: (e) => e.stopPropagation(),
                          onTouchMove: (e) => e.stopPropagation(),
                        }}
                      >
                        {/* Refresh Bookings */}
                        {!hideRefreshButton && (
                          <MenuItem
                            onClick={() => {
                              getAndSetBookedSlotsOfSelectedDate()
                              handleCaActionsMenuClose()
                            }}
                            disabled={bookedSlotsInDayLoading}
                            sx={{
                              pointerEvents: 'auto',
                              userSelect: 'none',
                              py: 1.5,
                              color: 'rgba(67, 56, 202, 0.9)',
                              fontSize: '14px',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                color: 'rgba(67, 56, 202, 1)',
                              },
                              '&.Mui-disabled': {
                                opacity: 0.5,
                                color: 'rgba(67, 56, 202, 0.5)',
                              },
                            }}
                          >
                            {bookedSlotsInDayLoading ? (
                              <CircularProgress size={16} sx={{ mr: 1.5, color: 'rgba(67, 56, 202, 0.7)' }} />
                            ) : (
                              <RestartAltIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                            )}
                            Refresh Calendar
                          </MenuItem>
                        )}

                        {/* Slot Duration */}
                        {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                          <>
                            <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                // Store the menu's anchor (3-dots button) before closing the menu
                                const menuAnchor = caActionsMenuAnchor
                                
                                // Close the menu first
                                handleCaActionsMenuClose()
                                
                                // Use setTimeout to ensure menu is closed before opening popover
                                // Use the menu's anchor (3-dots button) as the popover anchor for stability
                                setTimeout(() => {
                                  if (menuAnchor && document.body.contains(menuAnchor)) {
                                    setSlotDurationAnchorEl(menuAnchor)
                                  } else {
                                    // Fallback: create a synthetic anchor at a reasonable position
                                    const syntheticAnchor = document.createElement('div')
                                    syntheticAnchor.style.position = 'fixed'
                                    syntheticAnchor.style.top = '80px'
                                    syntheticAnchor.style.right = '20px'
                                    syntheticAnchor.style.width = '1px'
                                    syntheticAnchor.style.height = '1px'
                                    syntheticAnchor.style.zIndex = '9999'
                                    syntheticAnchor.style.pointerEvents = 'none'
                                    document.body.appendChild(syntheticAnchor)
                                    syntheticAnchorRef.current = syntheticAnchor
                                    setSlotDurationAnchorEl(syntheticAnchor)
                                  }
                                  
                                  // Reset local state to current value when opening
                                  if (eachSlotDurationInMins !== undefined) {
                                    setLocalSlotDuration(eachSlotDurationInMins)
                                  }
                                }, 100)
                              }}
                              sx={{
                                pointerEvents: 'auto',
                                userSelect: 'none',
                                py: 1.5,
                                color: 'rgba(67, 56, 202, 0.9)',
                                fontSize: '14px',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                  color: 'rgba(67, 56, 202, 1)',
                                },
                              }}
                            >
                              <HeightIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                              Slot Duration
                            </MenuItem>
                          </>
                        )}

                        {/* Sticky Header Toggle */}
                        <Divider sx={{ my: 0.5, borderColor: 'rgba(67, 56, 202, 0.1)' }} />
                        <MenuItem
                          onClick={() => {
                            setShowCalendarHeaderAfterScroll(!showCalendarHeaderAfterScroll)
                            handleCaActionsMenuClose()
                          }}
                          sx={{
                            pointerEvents: 'auto',
                            userSelect: 'none',
                            py: 1.5,
                            color: 'rgba(67, 56, 202, 0.9)',
                            fontSize: '14px',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              color: 'rgba(67, 56, 202, 1)',
                            },
                          }}
                        >
                          {showCalendarHeaderAfterScroll ? (
                            <PushPinIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                          ) : (
                            <PushPinOutlinedIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(67, 56, 202, 0.7)' }} />
                          )}
                          {showCalendarHeaderAfterScroll ? 'Hide' : 'Show'} Date Picker After Scroll
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Desktop-only filter row. Mobile actions now stay in the compact top row above. */}
            {isViewingCalendarRelatedTab && forMentor && !isMobile && (
              <>
                {/* Mobile: Action Buttons Row - Hamburger + 3-dots Menu */}
                {/* Hide on mobile when CalendarHeaderFloatingBar is visible to avoid duplicate buttons */}
                {isMobile && !isFloatingBarVisible && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 1,
                      mt: 0,
                    }}
                  >
                    {/* Hamburger Button - Only visible when floating bar is not visible */}
                    {onOpenBookedSlotsDrawer && (
                      <Tooltip title="View All Booked Slots" arrow>
                        <IconButton
                          size="small"
                          onClick={onOpenBookedSlotsDrawer}
                          sx={{
                            color: 'rgba(67, 56, 202, 0.7)',
                            backgroundColor: 'rgba(67, 56, 202, 0.04)',
                            border: '1px solid rgba(67, 56, 202, 0.15)',
                            padding: '6px',
                            minWidth: '36px',
                            minHeight: '36px',
                            width: '36px',
                            height: '36px',
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              borderColor: 'rgba(67, 56, 202, 0.3)',
                              color: 'rgba(67, 56, 202, 1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <ListIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* 3-dots Menu Button */}
                    <IconButton
                      size="small"
                      onClick={handleMobileMenuOpen}
                      sx={{
                        color: 'rgba(67, 56, 202, 0.7)',
                        backgroundColor: 'rgba(67, 56, 202, 0.04)',
                        border: '1px solid rgba(67, 56, 202, 0.15)',
                        padding: '6px',
                        minWidth: '36px',
                        minHeight: '36px',
                        width: '36px',
                        height: '36px',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          borderColor: 'rgba(67, 56, 202, 0.3)',
                          color: 'rgba(67, 56, 202, 1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    
                    {/* Mobile Menu */}
                    <Menu
                      anchorEl={mobileMenuAnchor}
                      open={Boolean(mobileMenuAnchor)}
                      onClose={handleMobileMenuClose}
                      PaperProps={{
                        sx: {
                          mt: 1.5,
                          minWidth: 200,
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                          borderRadius: '12px',
                          border: '1px solid rgba(67, 56, 202, 0.1)',
                        },
                      }}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      {/* Refresh */}
                      {!hideRefreshButton && (
                        <MenuItem
                          onClick={() => {
                            getAndSetBookedSlotsOfSelectedDate()
                            handleMobileMenuClose()
                          }}
                          disabled={bookedSlotsInDayLoading}
                          sx={{
                            py: 1.5,
                            '&.Mui-disabled': {
                              opacity: 0.6,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            {bookedSlotsInDayLoading ? (
                              <CircularProgress size={18} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                            ) : (
                              <StyledEventsRefreshIcon sx={{ fontSize: 18 }} />
                            )}
                            <Typography sx={{ fontSize: '14px' }}>Refresh Bookings</Typography>
                          </Box>
                        </MenuItem>
                      )}
                      
                      {/* Sticky Header Toggle */}
                      <MenuItem
                        onClick={() => {
                          setShowCalendarHeaderAfterScroll(!showCalendarHeaderAfterScroll)
                          handleMobileMenuClose()
                        }}
                        sx={{
                          py: 1.5,
                          backgroundColor: showCalendarHeaderAfterScroll ? 'rgba(67, 56, 202, 0.08)' : 'transparent',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          {showCalendarHeaderAfterScroll ? (
                            <PushPinIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
                          )}
                          <Typography sx={{ fontSize: '14px' }}>
                            {showCalendarHeaderAfterScroll ? 'Hide date picker after scrolling' : 'Show date picker after scrolling'}
                          </Typography>
                        </Box>
                      </MenuItem>
                      
                      {/* Adjust Slot Duration */}
                      {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                        <>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              // Store the menu's anchor before closing the menu
                              const menuAnchor = mobileMenuAnchor
                              
                              // Close the menu first
                              handleMobileMenuClose()
                              
                              // Use setTimeout to ensure menu is closed before opening popover
                              // Use the menu's anchor as the popover anchor
                              setTimeout(() => {
                                if (menuAnchor && document.body.contains(menuAnchor)) {
                                  setSlotDurationAnchorEl(menuAnchor)
                                } else {
                                  // Fallback: create a synthetic anchor at a reasonable position
                                  const syntheticAnchor = document.createElement('div')
                                  syntheticAnchor.style.position = 'fixed'
                                  syntheticAnchor.style.top = '80px'
                                  syntheticAnchor.style.right = '20px'
                                  syntheticAnchor.style.width = '1px'
                                  syntheticAnchor.style.height = '1px'
                                  syntheticAnchor.style.zIndex = '9999'
                                  syntheticAnchor.style.pointerEvents = 'none'
                                  document.body.appendChild(syntheticAnchor)
                                  syntheticAnchorRef.current = syntheticAnchor
                                  setSlotDurationAnchorEl(syntheticAnchor)
                                }
                                
                                // Reset local state to current value when opening
                                if (eachSlotDurationInMins !== undefined) {
                                  setLocalSlotDuration(eachSlotDurationInMins)
                                }
                              }, 100)
                            }}
                            sx={{ py: 1.5 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                              <HeightIcon sx={{ fontSize: 18 }} />
                              <Typography sx={{ fontSize: '14px' }}>Adjust Slot Duration</Typography>
                            </Box>
                          </MenuItem>
                          <Popover
                            open={slotDurationPopoverOpen}
                            anchorEl={slotDurationAnchorEl}
                            onClose={handleSlotDurationClose}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            disableRestoreFocus
                            disableAutoFocus
                            disableEnforceFocus
                            PaperProps={{
                              sx: {
                                padding: 0,
                                minWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                                maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px', md: '480px' },
                                borderRadius: '16px',
                                boxShadow: '0 8px 32px rgba(67, 56, 202, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                                border: '1px solid rgba(67, 56, 202, 0.15)',
                                overflow: 'hidden',
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 255, 0.98) 100%)',
                                backdropFilter: 'blur(20px)',
                                position: 'relative', // Ensure stable positioning
                              },
                            }}
                          >
                            <Box
                              sx={{
                                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 50%, rgba(99, 102, 241, 0.95) 100%)',
                                padding: { xs: '16px', sm: '20px' },
                                color: 'white',
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '16px', sm: '18px' },
                                  marginBottom: '4px',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                Slot Duration
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: '13px', sm: '14px' },
                                  opacity: 0.95,
                                  fontWeight: 500,
                                }}
                              >
                                Adjust how many minutes each calendar slot represents
                              </Typography>
                            </Box>
                            <Box sx={{ padding: { xs: '20px', sm: '24px' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <Box
                                sx={{
                                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%)',
                                  borderRadius: '12px',
                                  padding: { xs: '16px', sm: '20px' },
                                  border: '1px solid rgba(67, 56, 202, 0.12)',
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: 'rgba(67, 56, 202, 1)',
                                    fontSize: { xs: '24px', sm: '28px' },
                                    textAlign: 'center',
                                    marginBottom: '8px',
                                    letterSpacing: '-0.5px',
                                  }}
                                >
                                  {formatDuration(localSlotDuration)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    color: 'rgba(67, 56, 202, 0.7)',
                                    fontSize: { xs: '11px', sm: '12px' },
                                    fontWeight: 500,
                                  }}
                                >
                                  Current duration per slot
                                </Typography>
                              </Box>
                              <Box
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <Slider
                                  value={localSlotDuration}
                                  onChange={handleSlotDurationChange}
                                  min={MIN_SLOT_DURATION}
                                  max={MAX_SLOT_DURATION}
                                  step={5}
                                  marks={[
                                    { value: 5, label: '5 min' },
                                    { value: 15, label: '15 min' },
                                    { value: 30, label: '30 min' },
                                    { value: 60, label: '1h' },
                                    { value: 90, label: '90m' },
                                    { value: 120, label: '2h' },
                                  ]}
                                  valueLabelDisplay="on"
                                  slotProps={{
                                    valueLabel: {
                                      sx: {
                                        '& .MuiSlider-valueLabel': {
                                          backgroundColor: 'rgba(67, 56, 202, 0.95)',
                                          fontSize: { xs: '12px', sm: '13px' },
                                          fontWeight: 600,
                                          padding: { xs: '4px 8px', sm: '6px 10px' },
                                          borderRadius: '8px',
                                          '&::before': {
                                            display: 'none',
                                          },
                                        },
                                      },
                                    },
                                    thumb: {
                                      sx: {
                                        '&:hover': {
                                          boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                        },
                                        '&:active': {
                                          boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                        },
                                      },
                                    },
                                  }}
                                  sx={{
                                    color: 'rgba(67, 56, 202, 0.9)',
                                    height: { xs: 6, sm: 8 },
                                    '& .MuiSlider-thumb': {
                                      width: { xs: 20, sm: 24 },
                                      height: { xs: 20, sm: 24 },
                                      backgroundColor: 'rgba(67, 56, 202, 1)',
                                      border: '3px solid white',
                                      boxShadow: '0 2px 8px rgba(67, 56, 202, 0.4), 0 0 0 4px rgba(67, 56, 202, 0.1)',
                                      '&:hover': {
                                        boxShadow: '0 4px 12px rgba(67, 56, 202, 0.5), 0 0 0 6px rgba(67, 56, 202, 0.15)',
                                        transform: 'scale(1.1)',
                                      },
                                      '&:active': {
                                        transform: 'scale(1.15)',
                                      },
                                      '& .MuiSlider-valueLabel': {
                                        position: 'absolute !important',
                                        top: { xs: '-42px !important', sm: '-48px !important' },
                                        left: '50% !important',
                                        transform: 'translateX(-50%) !important',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                      },
                                    },
                                    '& .MuiSlider-valueLabel': {
                                      position: 'absolute !important',
                                      top: { xs: '-42px !important', sm: '-48px !important' },
                                      left: '50% !important',
                                      transform: 'translateX(-50%) !important',
                                      pointerEvents: 'none',
                                      zIndex: 1,
                                    },
                                    '& .MuiSlider-track': {
                                      backgroundColor: 'rgba(67, 56, 202, 0.9)',
                                      height: { xs: 6, sm: 8 },
                                      border: 'none',
                                    },
                                    '& .MuiSlider-rail': {
                                      backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                      height: { xs: 6, sm: 8 },
                                    },
                                    '& .MuiSlider-mark': {
                                      backgroundColor: 'rgba(67, 56, 202, 0.3)',
                                      width: { xs: 4, sm: 6 },
                                      height: { xs: 4, sm: 6 },
                                      borderRadius: '50%',
                                    },
                                    '& .MuiSlider-markLabel': {
                                      fontSize: { xs: '10px', sm: '11px' },
                                      fontWeight: 600,
                                      color: 'rgba(67, 56, 202, 0.7)',
                                      marginTop: { xs: '8px', sm: '10px' },
                                    },
                                  }}
                                  valueLabelFormat={(value) => formatDuration(value)}
                                />
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  justifyContent: 'space-between',
                                  alignItems: { xs: 'stretch', sm: 'center' },
                                  gap: { xs: 2, sm: 0 },
                                  paddingTop: { xs: '8px', sm: 0 },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'rgba(67, 56, 202, 0.65)',
                                    fontSize: { xs: '11px', sm: '12px' },
                                    fontWeight: 500,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  Range: {formatDuration(MIN_SLOT_DURATION)} to {formatDuration(MAX_SLOT_DURATION)}
                                </Typography>
                                <Button
                                  size="medium"
                                  startIcon={<RestartAltIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                  onClick={handleResetSlotDuration}
                                  disabled={!initialSlotDuration || localSlotDuration === initialSlotDuration}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    fontWeight: 600,
                                    padding: { xs: '8px 16px', sm: '10px 20px' },
                                    color: 'rgba(67, 56, 202, 0.9)',
                                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                    border: '2px solid rgba(67, 56, 202, 0.2)',
                                    borderRadius: '10px',
                                    minWidth: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                      backgroundColor: 'rgba(67, 56, 202, 0.15)',
                                      borderColor: 'rgba(67, 56, 202, 0.35)',
                                      color: 'rgba(67, 56, 202, 1)',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                                    },
                                    '&:active': {
                                      transform: 'translateY(0)',
                                    },
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.26)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      borderColor: 'rgba(0, 0, 0, 0.12)',
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  }}
                                >
                                  Reset
                                </Button>
                                <Button
                                  size="medium"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                  onClick={handleApplySlotDuration}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    fontWeight: 600,
                                    padding: { xs: '8px 20px', sm: '10px 24px' },
                                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                                    color: 'white',
                                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)',
                                    minWidth: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%)',
                                      boxShadow: '0 6px 16px rgba(67, 56, 202, 0.4)',
                                      transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  Apply
                                </Button>
                              </Box>
                            </Box>
                          </Popover>
                        </>
                      )}
                      
                      {/* Show Reports Data */}
                      {!hideToggleSwitchForReportRelatedData && !forMentor && (
                        <>
                          <Divider />
                          <MenuItem
                            onClick={() => {
                              setIncludeReportRelatedData((prev) => !prev)
                              handleMobileMenuClose()
                            }}
                            sx={{
                              py: 1.5,
                              backgroundColor: includeReportRelatedData ? 'rgba(67, 56, 202, 0.08)' : 'transparent',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                              {includeReportRelatedData ? (
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <DescriptionIcon sx={{ fontSize: 18 }} />
                              )}
                              <Typography sx={{ fontSize: '14px' }}>
                                {includeReportRelatedData ? 'Hide Reports Data' : 'Show Reports Data'}
                              </Typography>
                            </Box>
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </Box>
                )}

                {/* Desktop: Filter Pills Row - Hidden on mobile (and when lifted to the main header) */}
                {!isMobile && !hideFilterPills && (
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      mt: 0.5,
                    }}
                  >
                    <EDVCSessionsCompletedDetailsComponent
                      stacked={forMentor}
                      noMargins={compactUI}
                      compactUI={compactUI}
                      bookedSlotsInDay={bookedSlotsInDay}
                      cancelledCount={cancelledCount}
                      showCancelledOnly={showCancelledOnly}
                      setShowCancelledOnly={setShowCancelledOnly}
                      showCancelledChip={showCancelledChip}
                      includeReportRelatedData={includeReportRelatedData}
                      setIncludeReportRelatedData={setIncludeReportRelatedData}
                      hideToggleSwitchForReportRelatedData={true}
                      selectedFilterType={selectedFilterType}
                      setSelectedFilterType={setSelectedFilterType}
                      onOpenBookedSlotsDrawer={onOpenBookedSlotsDrawer}
                    />
                  </Box>
                )}
              </>
            )}
            {/* update bookings loader */}
          </div>
          {/* right side component */}
          <div style={{ flexShrink: 0, flexGrow: 0 }}>{RightSideExtraComponent}</div>
        </div>
      </div>
    </FloatingHeaderContainerDayCal>
    {isMobile && !collapsed && mobileReservedHeight > 0 && (
      <div aria-hidden style={{ height: `${mobileReservedHeight}px`, flexShrink: 0 }} />
    )}
    </>
  )
}
